import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resolveMobileTabs } from "@/lib/kiosk";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, X, LogOut, Loader2, CheckCircle2, ClipboardList, WifiOff, Download, CloudUpload, Globe, CalendarDays, ScanLine, Play, ClipboardCheck, Lock, ArrowLeft, Plus, AlertTriangle } from "lucide-react";
import { TR, LANGS } from "@/lib/i18n";
import { stringSim, bestMatchName, kgf, computeStockList } from "@/lib/excelCalc";
import { overigStandaard } from "@/lib/excelSeed";
import { grondstofAfboekingen, hulpstofAfboekingen } from "@/lib/voorraadHistory";
import { hamKaasBatchOptions } from "@/lib/hamKaasVoorraad";
import { REG_SEED, regTemplates, regInzendingen, nieuweInzending, subsForContext, subProduct, inzendingOpDag, regWeek, ontbrekendeRegistraties, openKalibratieTaken, nieuweKalibratieInzending, kalibratieZichtbaarVoor, buildAfwijkingAlert } from "./registratie/regFormSeed";
import { ProductPicker } from "@/components/ExcelUI";
import KlokPaneel from "./KioskKlok";
import { RegFormFiller, RegFormBuilder } from "./registratie/RegFormShared";
import { buildTraceOpties } from "./registratie/regFormSeed";

const QUEUE_KEY = "clazing_mobile_queue";
const LANG_KEY = "clazing_lang";
const MOBILE_DEVICE_ID_KEY = "clazing_mobile_device_id";
import { APP_VERSION, APP_BUILD } from "@/version";
import { halffabHoldMessage, isHalffabBookingBlocked } from "@/lib/halffabSafety";
import { saveProtectedChanges } from "@/lib/useProtectedSync";
import { saveRegistration } from "@/lib/registrationStorage";
import { ReleaseNoteViewer } from "@/components/ReleaseNote";
import { stockLedger, allocateHelperLots } from "@/lib/stockLedger";
import { matchRawScanBatch } from "@/lib/scanMatching";
const today = () => new Date().toISOString().slice(0, 10);
const recentDatumM = (d) => { const t = today(); return d && /^\d{4}-\d{2}-\d{2}$/.test(String(d)) && d <= t && !Number.isNaN(Date.parse(d)) ? d : t; };
const emptyForm = () => ({
  datum: today(), lijn: "", product: "", artikelnummer: "", geproduceerdKg: "", uitvalKg: "", uitvalReden: "",
  aantalMensen: "", productieUren: "", stillstandUren: "0", normKgUur: "",
  predust: "", predustKg: "", battermix: "", battermixKg: "", breader: "", breaderKg: "",
  hamkaas: "", hamkaasKg: "", hamkaasBatchId: "", hamkaasLots: [], hamkaasTrace: "", kruidenKg: "", grondstof: "", grondstofKg: "", restantKg: "", prijsGrondstofKg: "", halffabRunId: "", halffabLots: [], outputVerdeling: [], opmerking: "", photos: [], looptDoorFrituurolie: false,
});
const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const getYear = (s) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? 0 : d.getFullYear(); };
function getISOWeek(s) {
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return 0;
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}
const loadQueue = () => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; } };
const saveQueue = (q) => { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); };
const CACHE_KEY = "clazing_appdata_cache";
const loadCache = () => { try { localStorage.removeItem(CACHE_KEY); } catch (error) { /* opslag niet beschikbaar */ } return null; };
const saveCache = () => { try { localStorage.removeItem(CACHE_KEY); } catch (error) { /* quota */ } };
const DEVICE_KEY = "clazing_device_name";

async function compressImage(file, maxDim = 1600, quality = 0.7) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width >= height && width > maxDim) { height = Math.round((height * maxDim) / width); width = maxDim; }
      else if (height > width && height > maxDim) { width = Math.round((width * maxDim) / height); height = maxDim; }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => { URL.revokeObjectURL(url); resolve(blob || file); }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}
const blobToDataUrl = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
async function uploadBlob(blob, name) {
  const fd = new FormData();
  fd.append("file", blob, name || "foto.jpg");
  const res = await api.post("/upload", fd);
  return res.data.path;
}

export default function MobielInvoer() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(loadCache());
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(loadQueue());
  const [queueError, setQueueError] = useState("");
  const regBaselineRef = useRef(null);
  const pendingRegRef = useRef(null);
  const [installEvt, setInstallEvt] = useState(null);
  const [deviceName, setDeviceName] = useState(localStorage.getItem(DEVICE_KEY) || "");
  const [askDevice, setAskDevice] = useState(!localStorage.getItem(DEVICE_KEY) && !localStorage.getItem("clazing_device_asked"));
  const [lang, setLang] = useState(localStorage.getItem(LANG_KEY) || "nl");
  const [planView, setPlanView] = useState("dag");
  const fileRef = useRef(null);
  const [regView, setRegView] = useState("lijst");
  const [regTpl, setRegTpl] = useState(null);
  const [regTplList, setRegTplList] = useState(null);
  const [regSub, setRegSub] = useState(null);
  const [regData, setRegData] = useState({});
  const [regLijn, setRegLijn] = useState("");
  const [regMedew, setRegMedew] = useState("");
  const [regDatum, setRegDatum] = useState(today());
  const [regSaving, setRegSaving] = useState(false);
  const [regAuditDatum, setRegAuditDatum] = useState(today());
  const [regWarnHiddenDate, setRegWarnHiddenDate] = useState(() => localStorage.getItem("clazing_mreg_warn_hidden") || "");
  const [regBuilderInit, setRegBuilderInit] = useState(null);
  const invoerRef = useRef(null);
  const scanRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const pakScanRef = useRef(null);
  const [pakBusy, setPakBusy] = useState(false);
  const [pakItems, setPakItems] = useState(null);
  const grondPakScanRef = useRef(null);
  const [grondPakBusy, setGrondPakBusy] = useState(false);
  const [grondPakItems, setGrondPakItems] = useState(null);
  const divPakScanRef = useRef(null);
  const [divPakBusy, setDivPakBusy] = useState(false);
  const [divPakItems, setDivPakItems] = useState(null);
  const [divAfbSearch, setDivAfbSearch] = useState("");
  const [divAfbQty, setDivAfbQty] = useState({});
  const [receptScan, setReceptScan] = useState(null);
  const [receptScanning, setReceptScanning] = useState(false);
  const [receptSaving, setReceptSaving] = useState(false);
  const [receptSaveError, setReceptSaveError] = useState("");
  const receptBookingRef = useRef(null);
  const receptBusyRef = useRef(false);
  const receptScanRef = useRef(null);
  const [mode, setMode] = useState("definitief");
  const [scanned, setScanned] = useState({});
  const [mobScreen, setMobScreen] = useState("planning");
  const [orderFilter, setOrderFilter] = useState(false);
  const [vTab, setVTab] = useState("grond");
  const [vFilter, setVFilter] = useState("");
  const [divOpen, setDivOpen] = useState(false);
  const [divSearch, setDivSearch] = useState("");
  const [divQty, setDivQty] = useState({});
  const [divBusy, setDivBusy] = useState(false);
  const [syncState, setSyncState] = useState("checking");
  const [lastSyncAt, setLastSyncAt] = useState(0);
  const hl = (k) => (scanned[k] ? " ring-2 ring-amber-400 bg-amber-50" : "");
  const flushing = useRef(false);
  const cloudVersionRef = useRef(null);
  const cloudStoreVersionRef = useRef(null);
  const [mobileDeviceId] = useState(() => {
    const known = localStorage.getItem(MOBILE_DEVICE_ID_KEY);
    if (known) return known;
    const created = `mobile-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(MOBILE_DEVICE_ID_KEY, created);
    return created;
  });
  const token = localStorage.getItem("clazing_token");
  const t = (k) => (TR[lang] && TR[lang][k]) || TR.nl[k];
  const changeLang = (l) => { setLang(l); localStorage.setItem(LANG_KEY, l); };

  const loadCloud = async ({ silent = false } = {}) => {
    if (!navigator.onLine) {
      setSyncState("offline");
      return null;
    }
    if (!silent) setSyncState("checking");
    try {
      const r = await api.get("/appdata", {
        headers: { "Cache-Control": "no-cache" },
        params: { fresh: Date.now() },
      });
      const nd = r.data.data || { entries: [], costs: {}, lines: [], products: [], rawMaterials: [], helpers: [] };
      setData(nd);
      saveCache(nd);
      cloudVersionRef.current = r.data.updated_at || null;
      cloudStoreVersionRef.current = r.data.store_version;
      setLastSyncAt(Date.now());
      setSyncState("fresh");
      return { data: nd, updatedAt: r.data.updated_at || null, storeVersion: r.data.store_version };
    } catch (e) {
      console.debug("loadCloud offline, bestaande data behouden:", e);
      setSyncState("stale");
      return null;
    }
  };

  const resolvePhotos = async (photos) => {
    const out = [];
    for (const p of photos || []) {
      if (typeof p === "string") out.push(p);
      else if (p && p.dataUrl) { const blob = await (await fetch(p.dataUrl)).blob(); out.push(await uploadBlob(blob, "foto.jpg")); }
    }
    return out;
  };

  const saveMobileChanges = async (mutate) => {
    const cur = await api.get("/appdata", { headers: { "Cache-Control": "no-cache" }, params: { fresh: Date.now() } });
    if (!cur.data.data) throw new Error("Actuele cloudgegevens ontbreken; niets geboekt.");
    const before = cur.data.data;
    await saveProtectedChanges(before, mutate(before));
  };

  const pushToCloud = async (entry, cloudData, cloudVersion) => {
    if (!cloudData || !cloudVersion) throw new Error("Actuele cloudversie ontbreekt");
    await api.put(`/records/entries/${encodeURIComponent(entry.id)}`, { data: entry, base_data: null });
  };

  const isEmptyVal = (v) => v == null || v === "" || (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);
  const mergeRegData = (a, b) => {
    const out = { ...(a || {}) };
    for (const k of Object.keys(b || {})) {
      const bv = b[k], av = out[k];
      if (Array.isArray(bv)) out[k] = bv.length ? bv : (av || []);
      else if (bv && typeof bv === "object") out[k] = { ...(av && typeof av === "object" ? av : {}), ...Object.fromEntries(Object.entries(bv).filter(([, vv]) => !isEmptyVal(vv))) };
      else if (!isEmptyVal(bv)) out[k] = bv;
    }
    return out;
  };
  const applyOp = (cd, op) => {
    const base = cd || {};
    const p = op.payload;
    if (op.type === "entry") {
      const entries = (base.entries || []).filter((entry) => entry && typeof entry === "object");
      if (entries.some((entry) => String(entry.id) === String(p.id))) return base;
      return { ...base, entries: [p, ...entries] };
    }
    if (op.type === "divInkoop") return { ...base, diversenInkoop: [p, ...((base.diversenInkoop || []).filter((x) => x && typeof x === "object"))] };
    if (op.type === "divVerbruik") return { ...base, diversenVerbruik: [p, ...((base.diversenVerbruik || []).filter((x) => x && typeof x === "object"))] };
    if (op.type === "regForm") {
      const list = (base.regFormInzendingen || []).filter((x) => x && typeof x === "object");
      const idx = list.findIndex((s) => String(s.id) === String(p.id));
      if (idx === -1) return { ...base, regFormInzendingen: [p, ...list] };
      const copy = [...list]; copy[idx] = { ...list[idx], ...p, id: list[idx].id, data: mergeRegData(list[idx].data, p.data) };
      return { ...base, regFormInzendingen: copy };
    }
    return base;
  };
  const enqueueOp = (type, payload, baseline) => {
    const op = { __op: true, qid: Date.now() + Math.random(), type, payload, baseline, device: deviceName || user?.name || user?.email || "", ts: new Date().toISOString() };
    const q = [...loadQueue(), op];
    saveQueue(q); setQueue(q);
    setData((cd) => { const nd = applyOp(cd || {}, op); saveCache(nd); return nd; });
    if (navigator.onLine) setTimeout(() => flushQueue(), 50);
    return op;
  };
  const flushQueue = async () => {
    if (!navigator.onLine) return;
    if (flushing.current) return flushing.current;
    flushing.current = (async () => {
      const q = loadQueue();
      if (!q.length) return;
      const done = new Set();
      const keyOf = (item) => (item && item.__op ? item.qid : (item && item.id != null ? `entry:${item.id}` : `bad:${q.indexOf(item)}`));
      const pushEntry = async (entry) => {
        const e = { ...entry, photos: await resolvePhotos(entry.photos) };
        await api.put(`/records/entries/${encodeURIComponent(e.id)}`, { data: e, base_data: null });
      };
      for (const item of q) {
        if (!item || typeof item !== "object") { done.add(keyOf(item)); continue; }
        try {
          if (item.__op) {
            if (item.type === "regForm") await saveRegistration(item.payload, item.baseline ?? null);
            else if (item.type === "entry") await pushEntry(item.payload);
            else await saveMobileChanges((cd) => applyOp(cd, item));
          } else {
            // Oude offline productie-entry (zonder __op): alsnog veilig naar de server pushen.
            await pushEntry(item);
          }
          done.add(keyOf(item));
        } catch (error) { setQueueError(formatApiError(error)); }
      }
      const pending = loadQueue().filter((item) => !done.has(keyOf(item)));
      let localPersisted = true;
      try { saveQueue(pending); setQueue(pending); }
      catch { localPersisted = false; setQueueError("Serveracties kunnen al opgeslagen zijn, maar de lokale wachtrij kon niet worden bijgewerkt. Toestelopslag is vol of niet beschikbaar; sluit deze pagina niet af."); }
      if (localPersisted && !pending.length) setQueueError("");
      if (localPersisted && done.size) { toast.success(t("synced")); await loadCloud(); }
    })();
    try { await flushing.current; } finally { flushing.current = false; }
  };

  useEffect(() => {
    loadCloud();
    const on = () => { setOnline(true); loadCloud(); flushQueue(); };
    const off = () => { setOnline(false); setSyncState("offline"); };
    const bip = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("beforeinstallprompt", bip);
    if (navigator.onLine) flushQueue();
    const iv = setInterval(() => {
      if (!navigator.onLine) return;
      loadCloud({ silent: true });
      flushQueue();
    }, 10000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); window.removeEventListener("beforeinstallprompt", bip); clearInterval(iv); };
  }, []);

  useEffect(() => {
    const ping = () => {
      api.post("/presence", {
        version: APP_VERSION,
        build: APP_BUILD,
        device_id: mobileDeviceId,
        device_name: deviceName || "Tablet / mobiel",
        source: "mobile",
        sync_version: cloudVersionRef.current || "",
        sync_state: syncState,
        pending_count: loadQueue().length,
      }).catch(() => {});
    };
    ping();
    const timer = window.setInterval(ping, 10000);
    return () => { window.clearInterval(timer); };
  }, [mobileDeviceId, deviceName, syncState, queue.length]);

  useEffect(() => {
    const clear = () => {
      try {
        fetch(`${API}/presence?device_id=${encodeURIComponent(mobileDeviceId)}`, {
          method: "DELETE", keepalive: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("clazing_token") || sessionStorage.getItem("clazing_token") || ""}` },
        }).catch(() => {});
      } catch (e) { /* ignore */ }
    };
    window.addEventListener("pagehide", clear);
    return () => { window.removeEventListener("pagehide", clear); clear(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileDeviceId]);

  const lines = (data?.lines || []).filter((l) => l && typeof l === "object");
  const productieSynced = online && syncState === "fresh" && Date.now() - lastSyncAt < 45000;
  useEffect(() => {
    if (mobScreen === "productie") loadCloud();
  }, [mobScreen]);
  const products = (data?.products || []).filter((p) => p && typeof p === "object");
  const helpers = (data?.helpers || []).filter((h) => h && typeof h === "object");
  const rawMaterials = (data?.rawMaterials || []).filter((m) => m && typeof m === "object");
  const allowedMobile = resolveMobileTabs(user?.role, data?.permissions?.mobileTabs);
  useEffect(() => { if (allowedMobile && !allowedMobile.includes(mobScreen)) setMobScreen(allowedMobile[0]); /* eslint-disable-next-line */ }, [data, user?.role]);
  const halffabRunsM = (data?.halffabRuns || []).filter((r) => r && typeof r === "object");
  const hfOutM = (r) => { const g = (r.verbruik || []).reduce((a, v) => a + (v && v.categorie === "Grondstof" ? toNum(v.kg) : 0), 0); return g > 0 ? g : toNum(r.batch); };
  const halffabNamenM = [...new Set(halffabRunsM.map((r) => r.recept))].filter(Boolean);
  const halffabKostprijsM = (naam) => { const runs = halffabRunsM.filter((r) => r.recept === naam); const tb = runs.reduce((s, r) => s + hfOutM(r), 0); return tb > 0 ? runs.reduce((s, r) => s + (r.verbruik || []).reduce((a, v) => a + (v ? toNum(v.kg) * toNum(v.prijs) : 0), 0), 0) / tb : 0; };
  const halffabVoorraadM = (naam) => { const produced = halffabRunsM.filter((r) => r.recept === naam).reduce((s, r) => s + hfOutM(r), 0); const usedRuns = halffabRunsM.reduce((s, r) => s + (r.verbruik || []).reduce((a, v) => a + (v && v.categorie === "Halffabricaat" && v.naam === naam ? toNum(v.kg) : 0), 0), 0); const usedEntries = (data?.entries || []).filter((e) => e && typeof e === "object" && e.grondstof === naam).reduce((s, e) => s + Math.max(toNum(e.grondstofKg) - toNum(e.restantKg), 0), 0); return produced - usedRuns - usedEntries + toNum((data?.halffabCorrecties || {})[naam] || 0); };
  const _hfLotListM = (() => { const runs = [...halffabRunsM].sort((a, z) => (String(a.datum || "") < String(z.datum || "") ? -1 : String(a.datum || "") > String(z.datum || "") ? 1 : (a.id < z.id ? -1 : 1))); const seq = {}; const m = {}; runs.forEach((r) => { const dc = String(r.datum || "").replace(/-/g, ""); seq[dc] = (seq[dc] || 0) + 1; m[String(r.id)] = r.lotcode || `HF-${dc}-${String(seq[dc]).padStart(2, "0")}`; }); return m; })();
  const halffabLotOfM = (r) => (r ? (_hfLotListM[String(r.id)] || r.lotcode || "") : "");
  const halffabRunRestMapM = (() => {
    const map = {};
    const byNaam = {};
    halffabRunsM.forEach((run) => {
      if (run && run.recept) (byNaam[run.recept] = byNaam[run.recept] || []).push(run);
    });
    const entries = data?.entries || [];
    Object.keys(byNaam).forEach((naam) => {
      const runs = byNaam[naam].slice().sort((first, second) => (
        String(first.datum || "") < String(second.datum || "") ? -1 : 1
      ));
      const explicit = {};
      let pool = 0;
      entries.forEach((entry) => {
        if (!entry || entry.grondstof !== naam) return;
        const werkelijkVerbruikt = Math.max(
          toNum(entry.grondstofKg) - toNum(entry.restantKg),
          0,
        );
        let nogTeVerdelen = werkelijkVerbruikt;
        if (Array.isArray(entry.halffabLots) && entry.halffabLots.length) {
          entry.halffabLots.forEach((lot) => {
            if (!lot?.runId || nogTeVerdelen <= 0) return;
            const afboeking = Math.min(toNum(lot.kg), nogTeVerdelen);
            explicit[String(lot.runId)] = (explicit[String(lot.runId)] || 0) + afboeking;
            nogTeVerdelen -= afboeking;
          });
        } else if (entry.halffabRunId) {
          explicit[String(entry.halffabRunId)] = (
            explicit[String(entry.halffabRunId)] || 0
          ) + nogTeVerdelen;
          nogTeVerdelen = 0;
        }
        pool += nogTeVerdelen;
      });
      halffabRunsM.forEach((run) => (run.verbruik || []).forEach((line) => {
        if (!line || line.categorie !== "Halffabricaat" || line.naam !== naam) return;
        if (line.halffabRunId) {
          explicit[String(line.halffabRunId)] = (
            explicit[String(line.halffabRunId)] || 0
          ) + toNum(line.kg);
        } else {
          pool += toNum(line.kg);
        }
      }));
      runs.forEach((run) => {
        let rest = hfOutM(run) - (explicit[String(run.id)] || 0);
        const afboeken = Math.min(Math.max(rest, 0), Math.max(pool, 0));
        rest -= afboeken;
        pool -= afboeken;
        map[String(run.id)] = rest;
      });
    });
    return map;
  })();
  const halffabRunOptiesM = (naam) => halffabRunsM.filter((r) => r && r.recept === naam).map((r) => ({ id: String(r.id), lotcode: halffabLotOfM(r), rest: halffabRunRestMapM[String(r.id)] != null ? halffabRunRestMapM[String(r.id)] : hfOutM(r), datum: r.datum || "" })).sort((a, b) => (a.datum < b.datum ? -1 : 1));
  const onGrondstofM = (v) => { if (halffabNamenM.includes(v)) { setForm((f) => ({ ...f, grondstof: v, halffabRunId: "", halffabLots: [{ runId: "", kg: "" }], prijsGrondstofKg: String(halffabKostprijsM(v)), grondstofKg: "", restantKg: "0" })); } else { const m = rawMaterials.find((x) => x.naam === v); setForm((f) => ({ ...f, grondstof: v, halffabRunId: "", halffabLots: [], prijsGrondstofKg: m ? String(toNum(m.prijs)) : f.prijsGrondstofKg })); } };
  const updateHalffabLotsM = (lots) => { const clean = (lots || []).map((l) => ({ runId: String(l.runId || ""), kg: l.kg })); const totaal = clean.reduce((s, l) => s + toNum(l.kg), 0); setForm((f) => ({ ...f, halffabLots: clean, grondstofKg: totaal > 0 ? String(Number(totaal.toFixed(2))) : "", restantKg: "0", halffabRunId: (clean.find((l) => l.runId) || {}).runId || "" })); };
  const updateOutputVerdelingM = (rows) => { const clean = (rows || []).map((r) => ({ artikelnummer: String(r.artikelnummer || ""), kg: r.kg })); const tot = clean.reduce((s, r) => s + toNum(r.kg), 0); setForm((f) => ({ ...f, outputVerdeling: clean, geproduceerdKg: clean.length ? (tot > 0 ? String(Number(tot.toFixed(2))) : "0") : f.geproduceerdKg })); };
  const ingPriceM = (cat, naam) => { if (cat === "Grondstof") return toNum((rawMaterials.find((x) => x.naam === naam) || {}).prijs); if (cat === "Hulpstof") return toNum((helpers.find((x) => x.naam === naam) || {}).prijs); return 0; };
  const matchIngredientM = (it) => {
    const art = String(it.artikelnummer || "").trim().toLowerCase();
    if (art) {
      const m = rawMaterials.find((x) => String(x.artikelnummer || "").trim().toLowerCase() === art);
      if (m) return { categorie: "Grondstof", naam: m.naam, prijs: toNum(m.prijs) };
      const h = helpers.find((x) => String(x.artikelnr || "").trim().toLowerCase() === art);
      if (h) return { categorie: "Hulpstof", naam: h.naam, prijs: toNum(h.prijs) };
    }
    const low = String(it.naam || "").trim().toLowerCase();
    const rm = rawMaterials.find((x) => x.naam.toLowerCase() === low);
    if (rm) return { categorie: "Grondstof", naam: rm.naam, prijs: toNum(rm.prijs) };
    const h2 = helpers.find((x) => x.naam.toLowerCase() === low);
    if (h2) return { categorie: "Hulpstof", naam: h2.naam, prijs: toNum(h2.prijs) };
    const nm = String(it.naam || "").trim();
    const rmF = nm ? bestMatchName(nm, rawMaterials.map((x) => x.naam)) : "";
    const hF = nm ? bestMatchName(nm, helpers.map((x) => x.naam)) : "";
    const rs = rmF ? stringSim(nm, rmF) : 0; const hs = hF ? stringSim(nm, hF) : 0;
    if (Math.max(rs, hs) >= 0.45) {
      if (rs >= hs) return { categorie: "Grondstof", naam: rmF, prijs: ingPriceM("Grondstof", rmF) };
      return { categorie: "Hulpstof", naam: hF, prijs: ingPriceM("Hulpstof", hF) };
    }
    return { categorie: (it.categorie === "Grondstof" || it.categorie === "Hulpstof") ? it.categorie : "Grondstof", naam: nm, prijs: 0 };
  };
  // Getypte tracecode automatisch koppelen aan de ingeboekte partij (batchnr of eigen tracecode).
  const linkPartijM = (id, text) => setReceptScan((s) => ({ ...s, lines: (s.lines || []).map((x) => {
    if (x.id !== id) return x;
    if (x.categorie !== "Grondstof") return { ...x, partijnummer: text };
    const b = matchRawScanBatch({ ...x, partijnummer: text }, rawMaterials, helpers, data?.expeditie || []);
    if (!b) return { ...x, partijnummer: text, expeditieId: "", batchLots: [], eigenTracecode: "" };
    return { ...x, partijnummer: text, expeditieId: String(b.id), batchLots: [{ expeditieId: String(b.id), kg: toNum(x.kg) }], eigenTracecode: b.eigenTracecode || "", categorie: "Grondstof", naam: b.grondstof, prijs: String(toNum(b.prijs)) };
  }) }));
  const matchScanBatchM = (it) => matchRawScanBatch(it, rawMaterials, helpers, data?.expeditie || []);
  const grondVerbruikM = (n) => (data?.entries || []).reduce((s, e) => s + (e && typeof e === "object" && e.grondstof === n ? Math.max(toNum(e.grondstofKg) - toNum(e.restantKg), 0) : 0), 0) + halffabRunsM.reduce((s, r) => s + (r.verbruik || []).reduce((a, v) => a + (v && v.categorie === "Grondstof" && v.naam === n ? toNum(v.kg) : 0), 0), 0);
  // Resterende kg per grondstof-partij: alleen expliciet gebruik op/na de ontvangstdatum
  // mag een tracecode verminderen. Oud, ongekoppeld verbruik trekt een nieuw lot niet leeg.
  const grondBatchRestMapM = useMemo(() => stockLedger(data || {}, "Grondstof").batchRest, [data]);
  const grondBatchOptiesM = (naam) => (data?.expeditie || []).filter((b) => b && typeof b === "object" && b.grondstof === naam).map((b) => ({ id: String(b.id), rest: grondBatchRestMapM[String(b.id)] != null ? grondBatchRestMapM[String(b.id)] : toNum(b.kg), prijs: toNum(b.prijs), batchnr: b.batchnr || "", eigenTracecode: b.eigenTracecode || "", datum: b.datum || "", leverancier: b.leverancier || "" })).sort((a, b) => (a.datum < b.datum ? -1 : 1));
  const receptBatchLotsM = (line) => {
    if (Array.isArray(line?.batchLots) && line.batchLots.length) return line.batchLots;
    if (line?.expeditieId) return [{ expeditieId: String(line.expeditieId), kg: toNum(line.kg) }];
    return [];
  };
  const receptLotKgM = (line) => receptBatchLotsM(line).reduce((sumKg, lot) => sumKg + toNum(lot.kg), 0);
  const setLineBatchM = (id, lotIndex, bid) => setReceptScan((scan) => ({
    ...scan,
    lines: (scan.lines || []).map((line) => {
      if (line.id !== id) return line;
      const lots = receptBatchLotsM(line).map((lot) => ({ ...lot }));
      const batch = grondBatchOptiesM(line.naam).find((item) => item.id === bid);
      if (!batch) {
        lots[lotIndex] = { expeditieId: "", kg: "" };
        return { ...line, batchLots: lots, expeditieId: lots[0]?.expeditieId || "" };
      }
      const schaal = scanScaleM(scan);
      const usedElsewhere = (scan.lines || []).reduce((sumKg, otherLine) => (
        sumKg + receptBatchLotsM(otherLine).reduce((lineKg, lot, index) => (
          otherLine.id === id && index === lotIndex
            ? lineKg
            : lineKg + (String(lot.expeditieId) === String(bid)
              ? toNum(lot.kg) * schaal
              : 0)
        ), 0)
      ), 0);
      const otherLotsKg = lots.reduce((sumKg, lot, index) => (
        index === lotIndex ? sumKg : sumKg + toNum(lot.kg)
      ), 0);
      lots[lotIndex] = {
        expeditieId: String(bid),
        kg: Math.min(
          Math.max(toNum(line.kg) - otherLotsKg, 0),
          Math.max((batch.rest - usedElsewhere) / (schaal || 1), 0),
        ),
      };
      return {
        ...line,
        batchLots: lots,
        expeditieId: lots[0]?.expeditieId || "",
        eigenTracecode: batch.eigenTracecode || "",
        partijnummer: batch.batchnr || batch.eigenTracecode || line.partijnummer,
        prijs: batch.prijs > 0 ? String(batch.prijs) : line.prijs,
      };
    }),
  }));
  const setLineBatchLotKgM = (id, lotIndex, kg) => setReceptScan((scan) => ({
    ...scan,
    lines: (scan.lines || []).map((line) => {
      if (line.id !== id) return line;
      const lots = receptBatchLotsM(line).map((lot, index) => (
        index === lotIndex ? { ...lot, kg } : lot
      ));
      return { ...line, batchLots: lots, expeditieId: lots[0]?.expeditieId || "" };
    }),
  }));
  const addLineBatchLotM = (id) => setReceptScan((scan) => ({
    ...scan,
    lines: (scan.lines || []).map((line) => {
      if (line.id !== id) return line;
      const remainingKg = Math.max(toNum(line.kg) - receptLotKgM(line), 0);
      return {
        ...line,
        batchLots: [...receptBatchLotsM(line), { expeditieId: "", kg: remainingKg || "" }],
      };
    }),
  }));
  const removeLineBatchLotM = (id, lotIndex) => setReceptScan((scan) => ({
    ...scan,
    lines: (scan.lines || []).map((line) => {
      if (line.id !== id) return line;
      const lots = receptBatchLotsM(line).filter((_, index) => index !== lotIndex);
      return { ...line, batchLots: lots, expeditieId: lots[0]?.expeditieId || "" };
    }),
  }));
  const helperNames = (type) => helpers.filter((h) => h.type === type).map((h) => h.naam);
  const helperPrice = (type, naam) => helpers.find((h) => h.type === type && h.naam === naam)?.prijs || 0;
  const hamKaasFifoM = (naam) => hamKaasBatchOptions(
    data?.hamKaasBatches || [],
    data?.entries || [],
    naam,
  );
  const planning = useMemo(() => (data?.planning || []).filter((p) => p && typeof p === "object" && p.datum), [data]);
  const openPrepPlanning = planning.filter((p) => p.status !== "gereed" && !p.halffabRunId && !/gaarlijn/i.test(String(p.lijn || "")));
  const planItems = useMemo(() => {
    const y = getYear(form.datum), w = getISOWeek(form.datum);
    return planning
      .filter((p) => getYear(p.datum) === y && getISOWeek(p.datum) === w && (planView === "week" || p.datum === form.datum))
      .filter((p) => !form.lijn || p.lijn === form.lijn)
      .sort((a, b) => (a.datum < b.datum ? -1 : 1));
  }, [planning, form.datum, form.lijn, planView]);
  const planByLine = useMemo(() => {
    const m = {};
    planItems.forEach((p) => { (m[p.lijn] = m[p.lijn] || []).push(p); });
    return m;
  }, [planItems]);

  const recent = useMemo(() => {
    const q = queue.filter((e) => e && typeof e === "object" && !e.__op).map((e) => ({ ...e, _pending: true }));
    const cloud = (data?.entries || []).filter((e) => e && typeof e === "object").slice(0, 12);
    return [...q, ...cloud].slice(0, 15);
  }, [queue, data]);

  const divStock = (m) => {
    const ink = (data?.diversenInkoop || []).reduce((a, b) => a + (b && String(b.stamId) === String(m.id) ? toNum(b.aantal) : 0), 0);
    const vb = (data?.diversenVerbruik || []).reduce((a, b) => a + (b && String(b.stamId) === String(m.id) ? toNum(b.aantal) : 0), 0);
    return toNum(m.correctie) + ink - vb;
  };
  const fmtAant = (n) => Number(Number(n || 0).toFixed(2)).toLocaleString("nl-NL");
  const divList = useMemo(() => {
    const arr = (data?.diversenStam || []).filter((m) => m && typeof m === "object");
    const q = divSearch.trim().toLowerCase();
    const f = q ? arr.filter((m) => [m.naam, m.artikelnummer, m.categorie, m.leverancier].some((x) => String(x || "").toLowerCase().includes(q))) : arr;
    return [...f].sort((a, b) => ((b.inboekBijBinnenkomst ? 1 : 0) - (a.inboekBijBinnenkomst ? 1 : 0)) || String(a.naam || "").localeCompare(String(b.naam || "")));
  }, [data, divSearch]);
  const inboekDiversenM = (m) => {
    const qty = toNum(divQty[m.id]);
    if (qty <= 0) return toast.error("Vul een aantal in.");
    enqueueOp("divInkoop", { id: Date.now() + Math.random(), datum: today(), stamId: m.id, artikelnummer: m.artikelnummer || "", naam: m.naam || "", eenheid: m.eenheid || "", leverancier: m.leverancier || "", aantal: qty, prijs: toNum(m.prijs), batchnr: "", opmerking: "Inboek expeditie (mobiel)", device: deviceName || "" });
    setDivQty((q) => ({ ...q, [m.id]: "" }));
    toast.success(navigator.onLine ? `${qty} ${m.eenheid || ""} "${m.naam}" ingeboekt.` : `${qty} ${m.eenheid || ""} opgeslagen — synct zodra er internet is.`);
  };
  const divAfbList = useMemo(() => {
    const arr = (data?.diversenStam || []).filter((m) => m && typeof m === "object" && m.inboekBijBinnenkomst && !m.bevroren);
    const q = divAfbSearch.trim().toLowerCase();
    const f = q ? arr.filter((m) => [m.naam, m.artikelnummer, m.categorie].some((x) => String(x || "").toLowerCase().includes(q))) : arr;
    return [...f].sort((a, b) => String(a.naam || "").localeCompare(String(b.naam || "")));
  }, [data, divAfbSearch]);
  const afboekDiversenM = (m) => {
    const qty = toNum(divAfbQty[m.id]);
    if (qty <= 0) return toast.error("Vul een aantal in.");
    enqueueOp("divVerbruik", { id: Date.now() + Math.random(), datum: today(), stamId: m.id, artikelnummer: m.artikelnummer || "", naam: m.naam || "", eenheid: m.eenheid || "", aantal: qty, opmerking: "Productie (mobiel)", device: deviceName || "" });
    setDivAfbQty((q) => ({ ...q, [m.id]: "" }));
    toast.success(navigator.onLine ? `${qty} ${m.eenheid || ""} "${m.naam}" afgeboekt.` : `${qty} ${m.eenheid || ""} opgeslagen — synct zodra er internet is.`);
  };
  const onDivPakbonScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!navigator.onLine) { toast.error("Scannen kan alleen online."); if (divPakScanRef.current) divPakScanRef.current.value = ""; return; }
    setDivPakBusy(true);
    try {
      const stam = (data?.diversenStam || []).filter((m) => m && typeof m === "object");
      const all = []; let cnt = 0;
      for (const f of files) {
        let dataUrl; let mime = f.type || "application/pdf";
        if ((f.type || "").startsWith("image/")) { const blob = await compressImage(f, 2200, 0.85); dataUrl = await blobToDataUrl(blob); mime = "image/jpeg"; }
        else { dataUrl = await blobToDataUrl(f); }
        const res = await api.post("/ocr/pakbon-diversen", { file_base64: dataUrl, mime_type: mime });
        const R = res.data?.result || {};
        const lev = String(R.leverancier || ""); const dat = (R.datum && /^\d{4}-\d{2}-\d{2}$/.test(R.datum) && R.datum <= today()) ? R.datum : today(); const pnr = String(R.pakbonnummer || "");
        (R.items || []).forEach((it) => {
          const naam = String(it.omschrijving || "").trim(); const art = String(it.artikelnummer || "").trim();
          const match = stam.find((m) => (art && m.artikelnummer && String(m.artikelnummer).toLowerCase() === art.toLowerCase()) || (naam && String(m.naam).toLowerCase() === naam.toLowerCase()));
          all.push({ _id: cnt++, leverancier: match?.leverancier || lev, datum: dat, pakbonnummer: pnr, artikelnr: art || (match?.artikelnummer || ""), naam: match?.naam || naam, eenheid: match?.eenheid || String(it.eenheid || "") || "Stuk", aantal: toNum(it.aantal), prijs: match ? toNum(match.prijs) : "", batchnr: String(it.batchnr || ""), stamId: match?.id || "", bestaand: !!match });
        });
      }
      if (!all.length) { toast.error("Geen regels herkend."); return; }
      setDivPakItems(all);
      toast.success(`${all.length} regel(s) uit ${files.length} pakbon(nen) herkend.`);
    } catch (err) { toast.error("OCR mislukt: " + formatApiError(err)); }
    finally { setDivPakBusy(false); if (divPakScanRef.current) divPakScanRef.current.value = ""; }
  };
  const updateDivPakItem = (id, field, val) => setDivPakItems((arr) => (arr || []).map((it) => (it._id === id ? { ...it, [field]: val } : it)));
  const removeDivPakItem = (id) => setDivPakItems((arr) => (arr || []).filter((it) => it._id !== id));
  const confirmDivPakScan = async () => {
    const valid = (divPakItems || []).filter((it) => String(it.naam).trim() && toNum(it.aantal) > 0);
    if (!valid.length) return toast.error("Vul minstens één regel met naam en aantal in.");
    if (!navigator.onLine) return toast.error("Toevoegen kan alleen online.");
    try {
      await saveMobileChanges((cd) => {
        let stamArr = [...((cd.diversenStam || []).filter((m) => m && typeof m === "object"))];
        const inkopen = [];
        valid.forEach((it, i) => {
          const art = String(it.artikelnr || "").trim(); const naam = String(it.naam).trim();
          let m = stamArr.find((x) => (it.stamId && String(x.id) === String(it.stamId)) || (art && x.artikelnummer && String(x.artikelnummer).toLowerCase() === art.toLowerCase()) || String(x.naam).toLowerCase() === naam.toLowerCase());
          if (!m) { m = { id: Date.now() + Math.random() + i, artikelnummer: art, naam, categorie: "Overig", eenheid: it.eenheid || "Stuk", prijs: toNum(it.prijs), minVoorraad: 0, leverancier: it.leverancier || "", correctie: 0 }; stamArr = [...stamArr, m]; }
          inkopen.push({ id: Date.now() + Math.random() + 1000 + i, datum: it.datum || today(), stamId: m.id, artikelnummer: art || m.artikelnummer || "", naam: m.naam, eenheid: it.eenheid || m.eenheid || "", leverancier: it.leverancier || "", aantal: toNum(it.aantal), prijs: toNum(it.prijs) || toNum(m.prijs), batchnr: String(it.batchnr || ""), opmerking: it.pakbonnummer ? `Pakbon ${it.pakbonnummer}` : "Inboek expeditie (mobiel)" });
        });
        return { ...cd, diversenStam: stamArr, diversenInkoop: [...inkopen, ...((cd.diversenInkoop || []).filter((b) => b && typeof b === "object"))] };
      });
      const n = valid.length;
      setDivPakItems(null);
      toast.success(`${n} diversen-binnenkomst(en) toegevoegd aan de voorraad.`);
      loadCloud();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const regTpls = regTemplates(data);
  const regBouwerRollen = (data?.regFormConfig?.bouwerRollen || ["Bedrijfsleiding"]);
  const regCanBuild = user?.role === "Bedrijfsleiding" || regBouwerRollen.includes(user?.role);
  const plannedProduct = (lijn, datum) => {
    const p = (data?.planning || []).find((x) => x && typeof x === "object" && x.datum === datum && String(x.lijn || "") === String(lijn));
    return p ? String(p.product || "") : "";
  };
  const productOpties = products.map((p) => ({ naam: p.naam, art: p.artikelnummer || "" })).filter((p) => p.naam);
  const traceOpties = useMemo(() => buildTraceOpties(data), [data]);
  const regHuidigProduct = (tpl, d) => { let prod = ""; (tpl?.secties || []).forEach((sec) => { if (sec.type === "velden") (sec.velden || []).forEach((v) => { if (v.vt === "product" && (d[sec.id] || {})[v.id]) prod = (d[sec.id])[v.id]; }); }); return prod; };
  const regLijnOpties = (() => { const s = []; regTpls.filter((t) => t.actief !== false && t.perLijn).forEach((t) => (t.lijnOpties || []).forEach((l) => { if (l && !s.includes(l)) s.push(l); })); return s.length ? s : lines.map((l) => l.naam).filter(Boolean); })();
  const openTplList = (tpl) => {
    if (tpl.perLijn && !regLijn) { toast.error("Kies eerst een lijn — dit formulier wordt per lijn ingevuld."); return; }
    setRegTplList(tpl);
    setRegView("kies");
  };
  const openExistingSub = (tpl, sub) => {
    regBaselineRef.current = sub;
    pendingRegRef.current = null;
    setRegTpl(tpl);
    setRegSub(sub);
    setRegData(JSON.parse(JSON.stringify(sub.data || {})));
    setRegView("invullen");
  };
  const openNewSub = (tpl) => {
    regBaselineRef.current = null;
    pendingRegRef.current = null;
    setRegTpl(tpl);
    const ctx = { datum: regDatum, week: regWeek(regDatum), lijn: regLijn, medewerker: regMedew };
    const base = {};
    const prod = plannedProduct(regLijn, regDatum);
    if (prod) tpl.secties.forEach((sec) => { if (sec.type === "velden") (sec.velden || []).forEach((vd) => { if (vd.vt === "product") { base[sec.id] = base[sec.id] || {}; base[sec.id][vd.id] = prod; } }); });
    setRegSub(nieuweInzending(tpl, ctx, user));
    setRegData(base);
    setRegView("invullen");
  };
  const openKalibratieTaak = (taak) => {
    regBaselineRef.current = null;
    pendingRegRef.current = null;
    setRegTplList(null);
    setRegTpl(taak.tpl);
    setRegSub(nieuweKalibratieInzending(taak, user));
    setRegData({});
    setRegView("invullen");
  };
  const persistReg = async (subObj, dataObj, afronden) => {
    if (regSaving) return;
    setRegSaving(true);
    try {
      if (!pendingRegRef.current) {
        const merged = { ...subObj, data: dataObj, updatedAt: new Date().toISOString(), device: deviceName || user?.name || "" };
        if (afronden) { merged.status = "afgerond"; merged.afgerondDoor = user?.name || user?.email || ""; merged.afgerondTs = new Date().toISOString(); }
        pendingRegRef.current = enqueueOp("regForm", merged, regBaselineRef.current);
      }
      const op = pendingRegRef.current;
      if (!navigator.onLine) {
        setQueueError("Alleen op dit toestel bewaard, nog niet op de server. Laat de app staan tot de verbinding terug is.");
        toast.info("Lokaal concept bewaard — nog niet op de server opgeslagen.");
        return;
      }
      await flushQueue();
      if (loadQueue().some((item) => item.qid === op.qid)) await flushQueue();
      if (loadQueue().some((item) => item.qid === op.qid)) throw new Error("Dit formulier wacht nog op serverbevestiging. Probeer opnieuw; je invoer blijft bewaard.");
      const live = await loadCloud();
      const saved = live?.data?.regFormInzendingen?.find((s) => String(s.id) === String(op.payload.id));
      if (!saved || JSON.stringify(saved) !== JSON.stringify(op.payload)) throw new Error("Opnieuw openen op de server kon niet worden bevestigd.");
      regBaselineRef.current = saved; pendingRegRef.current = null;
      setRegSub(saved); setQueueError("");
      toast.success(saved.status === "afgerond" ? "Formulier afgerond en op de server gecontroleerd." : "Op de server opgeslagen en gecontroleerd.");
      if (saved.status === "afgerond") {
        const alertPayload = buildAfwijkingAlert(saved, regTpl);
        if (alertPayload) api.post("/registraties/afwijking-alert", alertPayload).catch(() => {});
        setRegView(regTplList ? "kies" : "lijst");
      }
    } catch (error) { setQueueError(formatApiError(error)); toast.error(`Niet bevestigd: ${formatApiError(error)}`, { duration: 15000 }); }
    finally { setRegSaving(false); }
  };
  const saveRegForm = (afronden) => persistReg(regSub, regData, afronden);
  const heropenReg = () => persistReg({ ...regSub, status: "open", afgerondDoor: "", afgerondTs: "" }, regData, false);
  const saveRegTemplate = async (tpl) => {
    if (!navigator.onLine) return toast.error("Opslaan kan alleen online.");
    if (!String(tpl.titel || "").trim()) return toast.error("Geef het formulier een titel.");
    const id = tpl.id || `tpl${Date.now()}`;
    try {
      await saveMobileChanges((cd) => {
        const base = (cd.regFormSjablonen && cd.regFormSjablonen.length ? cd.regFormSjablonen : REG_SEED).filter((x) => x && typeof x === "object");
        const exists = base.some((x) => x.id === id);
        const saved = { ...tpl, id, actief: tpl.actief !== false };
        return { ...cd, regFormSjablonen: exists ? base.map((x) => (x.id === id ? saved : x)) : [...base, saved] };
      });
      loadCloud(); setRegView("lijst"); setRegBuilderInit(null);
      toast.success("Formulier opgeslagen.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const set = (k, v) => setForm((prev) => {
    const next = { ...prev, [k]: v };
    if (k === "product") {
      const p = products.find((x) => x.naam === v);
      if (p) {
        next.normKgUur = p.normKgUur;
        next.artikelnummer = p.artikelnummer || "";
        next.looptDoorFrituurolie = p.looptDoorFrituurolie === true;
      }
    }
    return next;
  });

  const photoSrc = (p) => (typeof p === "string" ? `${API}/files/${p}?auth=${token}` : p?.dataUrl);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of files) {
        const blob = await compressImage(f);
        if (navigator.onLine) { try { added.push(await uploadBlob(blob, (f.name || "foto").replace(/\.[^.]+$/, "") + ".jpg")); continue; } catch (e) { console.debug("upload mislukt, val terug op lokale opslag:", e); } }
        added.push({ dataUrl: await blobToDataUrl(blob) });
      }
      setForm((prev) => ({ ...prev, photos: [...prev.photos, ...added] }));
      toast.success(t("fotoAdded").replace("{n}", added.length));
    } catch (err) { toast.error(t("fotoFailed") + formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = (idx) => setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));

  const onScanForm = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setScanning(true);
    try {
      const blob = await compressImage(file, 2000, 0.85);
      const dataUrl = await blobToDataUrl(blob);
      const res = await api.post("/ocr/production-form", { image_base64: dataUrl });
      const f = res.data.fields || {};
      const filled = {};
      ["datum", "lijn", "product", "artikelnummer", "geproduceerdKg", "uitvalKg", "aantalMensen", "productieUren", "stillstandUren", "kruidenKg", "predustKg", "battermixKg", "breaderKg", "hamkaasKg", "opmerking"].forEach((k) => { if (f[k] !== undefined && f[k] !== null && String(f[k]).trim() !== "") filled[k] = true; });
      setScanned(filled); setTimeout(() => setScanned({}), 6000);
      setForm((prev) => {
        const next = { ...prev };
        ["datum", "lijn", "product", "artikelnummer", "geproduceerdKg", "uitvalKg", "aantalMensen", "productieUren", "stillstandUren", "kruidenKg", "predustKg", "battermixKg", "breaderKg", "hamkaasKg", "opmerking"].forEach((k) => {
          if (f[k] !== undefined && f[k] !== null && String(f[k]).trim() !== "") next[k] = String(f[k]);
        });
        // Artikelnummer heeft voorrang op de (mogelijk anderstalige) gescande naam.
        const art = String(next.artikelnummer || "").trim().toLowerCase();
        const p = (art && products.find((x) => String(x.artikelnummer || "").trim().toLowerCase() === art)) || products.find((x) => x.naam === next.product);
        if (p) { next.product = p.naam; next.normKgUur = p.normKgUur; next.artikelnummer = p.artikelnummer || next.artikelnummer; next.looptDoorFrituurolie = p.looptDoorFrituurolie === true; }
        return next;
      });
      toast.success(t("scanKlaar"));
    } catch (err) { toast.error(t("scanFout") + formatApiError(err)); }
    finally { setScanning(false); if (scanRef.current) scanRef.current.value = ""; }
  };

  const onReceptScanM = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (!navigator.onLine) { toast.error("Scannen kan alleen online."); if (receptScanRef.current) receptScanRef.current.value = ""; return; }
    setReceptScanning(true);
    try {
      let dataUrl, mime = file.type || "application/pdf";
      if ((file.type || "").startsWith("image/")) { const blob = await compressImage(file, 2200, 0.85); dataUrl = await blobToDataUrl(blob); mime = "image/jpeg"; }
      else { dataUrl = await blobToDataUrl(file); }
      const res = await api.post("/ocr/recept-form", { file_base64: dataUrl, mime_type: mime });
      const R = res.data?.result || {};
      const lines = (R.ingredienten || []).map((it, i) => {
        const m = matchIngredientM(it);
        const partij = String(it.partijnummer || "").trim();
        const b = m.categorie === "Grondstof" ? matchScanBatchM({ ...it, naam: m.naam, categorie: m.categorie }) : null;
        const kg = it.kg != null ? String(it.kg) : "";
        return {
          id: Date.now() + Math.random() + i,
          categorie: m.categorie,
          naam: m.naam,
          artikelnummer: String(it.artikelnummer || ""),
          partijnummer: partij || (b ? String(b.batchnr || "") : ""),
          expeditieId: b ? String(b.id) : "",
          eigenTracecode: b ? String(b.eigenTracecode || "") : "",
          batchLots: b ? [{
            expeditieId: String(b.id),
            kg: Math.min(toNum(kg), Math.max(grondBatchRestMapM[String(b.id)] || 0, 0)),
          }] : [],
          kg,
          prijs: b ? String(toNum(b.prijs)) : (m.prijs ? String(m.prijs) : ""),
        };
      }).filter((() => { const s = new Set(); return (l) => { const k = `${l.artikelnummer}|${l.partijnummer}|${l.naam}|${l.kg}`; if (s.has(k)) return false; s.add(k); return true; }; })());
      const _nm = String(R.halffabricaat || "").toLowerCase(); const _words = _nm.split(/\s+/).filter((w) => w.length >= 3);
      const _sug = openPrepPlanning.find((p) => _words.some((w) => String(p.product || "").toLowerCase().includes(w)));
      receptBookingRef.current = null;
      setReceptSaveError("");
      setReceptScan({ naam: R.halffabricaat || "", datum: recentDatumM(R.datum), rondjes: toNum(R.rondjes) > 0 ? String(R.rondjes) : "", hoofdKg: toNum(R.hoofdgrondstofKg) > 0 ? String(R.hoofdgrondstofKg) : (toNum(R.begingewicht) > 0 ? String(R.begingewicht) : ""), planId: _sug ? String(_sug.id) : "", planLijn: "", planDatum: R.datum || today(), lines });
      toast.success("Formulier gescand \u2014 controleer en bevestig.");
    } catch (err) { toast.error("Scan mislukt: " + formatApiError(err)); }
    finally { setReceptScanning(false); if (receptScanRef.current) receptScanRef.current.value = ""; }
  };
  const scanScaleM = (scan) => { const lines = scan.lines || []; const grond = lines.filter((l) => (l.categorie || "Grondstof") === "Grondstof"); const baseMain = grond.length ? grond.reduce((s, l) => s + toNum(l.kg), 0) : (lines[0] ? toNum(lines[0].kg) : 0); const hoofd = toNum(scan.hoofdKg); if (hoofd > 0 && baseMain > 0) { const ratio = hoofd / baseMain; return Math.abs(ratio - 1) < 0.02 ? 1 : ratio; } const r = toNum(scan.rondjes); if (r > 0) return r; return 1; };
  const grondNamenM = [...new Set([...rawMaterials.map((m) => m.naam), ...halffabNamenM])].filter(Boolean).sort((a, b) => String(a).localeCompare(String(b)));
  const setLineNaamM = (id, nn) => setReceptScan((s) => ({ ...s, lines: (s.lines || []).map((x) => {
    if (x.id !== id) return x;
    const known = rawMaterials.find((m) => m.naam === nn);
    if (!known) return { ...x, naam: nn };
    const b = matchScanBatchM({ naam: nn, artikelnummer: known.artikelnummer || "", partijnummer: "" });
    return { ...x, naam: nn, categorie: "Grondstof", artikelnummer: known.artikelnummer || x.artikelnummer, prijs: b ? String(toNum(b.prijs)) : String(ingPriceM("Grondstof", nn)), expeditieId: b ? String(b.id) : "", batchLots: b ? [{ expeditieId: String(b.id), kg: Math.min(toNum(x.kg), Math.max(grondBatchRestMapM[String(b.id)] || 0, 0)) }] : [], eigenTracecode: b ? String(b.eigenTracecode || "") : (x.eigenTracecode || ""), partijnummer: b ? String(b.batchnr || "") : x.partijnummer };
  }) }));
  const addGrondLineM = () => setReceptScan((s) => ({ ...s, lines: [...((s && s.lines) || []), { id: Date.now() + Math.random(), categorie: "Grondstof", naam: "", artikelnummer: "", partijnummer: "", expeditieId: "", kg: "", prijs: "" }] }));
  const persistReceptBooking = async () => {
    if (receptBusyRef.current) return;
    receptBusyRef.current = true;
    setReceptSaving(true);
    setReceptSaveError("");
    const booking = receptBookingRef.current;
    try {
      if (!booking.writes) {
        const current = await api.get("/appdata");
        const cd = current.data.data;
        if (!cd) throw new Error("Actuele cloudgegevens ontbreken.");
        if (["expeditie", "halffabRuns", "entries", "rawMaterials", "hulpstofInkoop", "helpers", "voorraadTellingen", "afkeur"].some((key) => JSON.stringify(cd[key] || []) !== JSON.stringify(booking.stockBase[key] || []))) {
          receptBookingRef.current = null;
          await loadCloud();
          throw new Error("Voorraad is intussen gewijzigd. Controleer de partijen opnieuw; er is nog niets geboekt.");
        }
        const { run, scan } = booking;
        const writes = [{ key: "halffabRuns", data: run, base_data: null }];
        if (!(cd.recepten || []).some((r) => String(r.naam || "").toLowerCase() === run.recept.toLowerCase())) {
          const total = run.verbruik.reduce((sum, v) => sum + v.kg, 0) || 1;
          writes.push({ key: "recepten", base_data: null, data: { id: `${run.id}-recept`, naam: run.recept, eindproduct: "", ingredienten: run.verbruik.map((v, i) => ({ id: `${run.id}-ing-${i}`, categorie: v.categorie, naam: v.naam, percentage: Number((v.kg / total * 100).toFixed(2)) })) } });
        }
        if (scan.planId) {
          const plan = (cd.planning || []).find((p) => String(p.id) === String(scan.planId));
          if (!plan) throw new Error("De gekozen planning bestaat niet meer. Controleer de planning voordat je boekt.");
          writes.push({ key: "planning", base_data: plan, data: { ...plan, status: "gereed", halffabRunId: run.id, gebooktKg: run.batch } });
        } else if (scan.planLijn) {
          writes.push({ key: "planning", base_data: null, data: { id: `${run.id}-plan`, datum: scan.planDatum || run.datum, lijn: scan.planLijn, product: run.recept, artikelnummer: "", hoeveelheid: Number(run.batch.toFixed(1)), eenheid: "kg", soort: "voorbereiding", opmerking: toNum(scan.rondjes) > 0 ? `${scan.rondjes} rondjes` : "", status: "", halffabRunId: run.id, halffabNaam: run.recept } });
        }
        booking.writes = writes;
      }
      for (const write of booking.writes) {
        await api.put(`/records/${write.key}/${encodeURIComponent(write.data.id)}`, { data: write.data, base_data: write.base_data });
      }
      const saved = await loadCloud();
      const stored = saved?.data?.halffabRuns?.find((r) => String(r.id) === String(booking.run.id));
      if (!stored || JSON.stringify(stored) !== JSON.stringify(booking.run)) throw new Error("De serverbevestiging kon niet worden gecontroleerd.");
      receptBookingRef.current = null;
      setReceptScan(null);
      toast.success(`${stored.recept} opgeslagen en gecontroleerd · ${kgf(stored.batch)} · €${stored.kostprijsKg.toFixed(2)}/kg`);
    } catch (err) {
      const message = `${formatApiError(err)} De boeking is niet volledig bevestigd. Probeer opnieuw met deze scan; dezelfde boekingsnummers worden gebruikt.`;
      setReceptSaveError(message);
      toast.error(message, { duration: 15000 });
    } finally { receptBusyRef.current = false; setReceptSaving(false); }
  };
  const confirmReceptScan = async () => {
    if (receptBusyRef.current) return;
    if (!navigator.onLine) return toast.error("Boeken kan alleen met een cloudverbinding. Je scan blijft open.");
    if (receptBookingRef.current) return persistReceptBooking();
    const scan = receptScan; if (!scan) return;
    const naam = String(scan.naam || "").trim();
    if (!naam) return toast.error("Vul een naam in.");
    const lines = (scan.lines || []).filter((l) => String(l.naam).trim() && toNum(l.kg) > 0);
    if (!lines.length) return toast.error("Geen geldige regels.");
    const exped = (data?.expeditie || []).filter((x) => x && typeof x === "object");
    const losGrond = [...new Set(lines.filter((line) => {
      if ((line.categorie || "Grondstof") !== "Grondstof") return false;
      if (!exped.some((item) => item.grondstof === line.naam)) return false;
      return !receptBatchLotsM(line).length || Math.abs(receptLotKgM(line) - toNum(line.kg)) > 0.01;
    }).map((line) => line.naam))];
    if (losGrond.length) return toast.error(`Kies de partij (tracecode) voor: ${losGrond.join(", ")}. Typ het partijnummer of kies de partij in de lijst.`);
    const scale = scanScaleM(scan);
    const batchGebruik = {};
    lines.forEach((line) => {
      if ((line.categorie || "Grondstof") !== "Grondstof") return;
      receptBatchLotsM(line).forEach((lot) => {
        if (!lot.expeditieId) return;
        batchGebruik[lot.expeditieId] = (batchGebruik[lot.expeditieId] || 0)
          + toNum(lot.kg) * scale;
      });
    });
    const teVeelPerPartij = Object.entries(batchGebruik).map(([batchId, kg]) => {
      const batch = exped.find((item) => String(item.id) === String(batchId));
      return { batch, kg };
    }).filter(({ batch, kg }) => (
      batch && kg > toNum(grondBatchRestMapM[String(batch.id)]) + 0.001
    ));
    if (teVeelPerPartij.length) {
      return toast.error(
        `Partij-restant overschreden: ${teVeelPerPartij.map(({ batch, kg }) => (
          `${batch.batchnr || batch.eigenTracecode || "partij"} (${kgf(grondBatchRestMapM[String(batch.id)])} beschikbaar, ${kgf(kg)} gekozen)`
        )).join(" · ")}.`,
      );
    }
    const verbruik = lines.flatMap((line) => {
      const categorie = line.categorie || "Grondstof";
      if (categorie === "Hulpstof") return allocateHelperLots(data || {}, line.naam, toNum(line.kg) * scale, line.partijnummer || "").map((lot) => ({ categorie, naam: line.naam, kg: lot.kg, prijs: toNum(line.prijs) || ingPriceM(categorie, line.naam), artikelnummer: line.artikelnummer || "", partijnummer: lot.tracecode, batchnr: lot.tracecode }));
      if (categorie !== "Grondstof" || !receptBatchLotsM(line).length) {
        return [{
          categorie,
          naam: line.naam,
          kg: toNum(line.kg) * scale,
          prijs: toNum(line.prijs) || ingPriceM(categorie, line.naam),
          artikelnummer: line.artikelnummer || "",
          partijnummer: line.partijnummer || "",
          expeditieId: line.expeditieId || "",
          eigenTracecode: line.eigenTracecode || "",
        }];
      }
      return receptBatchLotsM(line).filter((lot) => lot.expeditieId && toNum(lot.kg) > 0).map((lot) => {
        const batch = grondBatchOptiesM(line.naam).find((item) => item.id === String(lot.expeditieId));
        return {
          categorie,
          naam: line.naam,
          kg: toNum(lot.kg) * scale,
          prijs: toNum(batch?.prijs) || toNum(line.prijs) || ingPriceM(categorie, line.naam),
          artikelnummer: line.artikelnummer || "",
          partijnummer: batch?.batchnr || batch?.eigenTracecode || line.partijnummer || "",
          expeditieId: String(lot.expeditieId),
          eigenTracecode: batch?.eigenTracecode || line.eigenTracecode || "",
        };
      });
    });
    const b = verbruik.reduce((s, v) => s + (v.categorie === "Grondstof" ? v.kg : 0), 0) || verbruik.reduce((s, v) => s + v.kg, 0);
    const kostprijsKg = verbruik.reduce((s, v) => s + v.kg * v.prijs, 0) / (b || 1);
    const grondPerNaam = {};
    verbruik.forEach((v) => { if (v.categorie === "Grondstof") grondPerNaam[v.naam] = (grondPerNaam[v.naam] || 0) + v.kg; });
    const tekorten = Object.entries(grondPerNaam).map(([n, kg]) => ({ n, kg, beschikbaar: stockLedger(data || {}, "Grondstof").lots.filter((l) => l.names.includes(n)).reduce((total, l) => total + l.rest, 0) })).filter((t) => t.kg > t.beschikbaar + 0.001);
    if (tekorten.length && !window.confirm("Let op: onvoldoende voorraad voor:\n\n" + tekorten.map((t) => `\u2022 ${t.n}: beschikbaar ${Math.max(t.beschikbaar, 0).toFixed(1)} kg, nodig ${t.kg.toFixed(1)} kg`).join("\n") + "\n\nWil je toch doorboeken?")) return;
    const planId = scan.planId;
    const planRow = planId ? planning.find((p) => String(p.id) === String(planId)) : null;
    const run = { id: `hf-${crypto.randomUUID()}`, datum: scan.datum || today(), recept: naam, batch: b, kostprijsKg, door: user?.name || user?.email || "-", verbruik, bron: "scan", planId: planId || null, planProduct: planRow ? planRow.product : "" };
    receptBookingRef.current = { run, scan: { ...scan }, stockBase: data };
    await persistReceptBooking();
  };

  const onPakbonScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!navigator.onLine) { toast.error("Scannen kan alleen online."); if (pakScanRef.current) pakScanRef.current.value = ""; return; }
    setPakBusy(true);
    try {
      const all = []; let cnt = 0;
      for (const f of files) {
        let dataUrl; let mime = f.type || "application/pdf";
        if ((f.type || "").startsWith("image/")) { const blob = await compressImage(f, 2200, 0.85); dataUrl = await blobToDataUrl(blob); mime = "image/jpeg"; }
        else { dataUrl = await blobToDataUrl(f); }
        const res = await api.post("/ocr/pakbon", { file_base64: dataUrl, mime_type: mime });
        const R = res.data?.result || {};
        const lev = String(R.leverancier || ""); const dat = (R.datum && /^\d{4}-\d{2}-\d{2}$/.test(R.datum) && R.datum <= today()) ? R.datum : today(); const pnr = String(R.pakbonnummer || "");
        (R.items || []).forEach((it) => {
          const naam = String(it.omschrijving || "").trim(); const art = String(it.artikelnummer || "").trim();
          const match = helpers.find((h) => (art && h.artikelnr && String(h.artikelnr).toLowerCase() === art.toLowerCase()) || (naam && h.naam.toLowerCase() === naam.toLowerCase()));
          all.push({ _id: cnt++, leverancier: lev, datum: dat, pakbonnummer: pnr, artikelnr: art || (match?.artikelnr || ""), naam: match?.naam || naam, type: match?.type || it.type || "Breader", kg: toNum(it.kg), prijs: match ? toNum(match.prijs) : "", batchnr: String(it.batchnr || ""), bestaand: !!match });
        });
      }
      if (!all.length) { toast.error("Geen regels herkend."); return; }
      setPakItems(all);
      toast.success(`${all.length} regel(s) uit ${files.length} pakbon(nen) herkend.`);
    } catch (err) { toast.error("OCR mislukt: " + formatApiError(err)); }
    finally { setPakBusy(false); if (pakScanRef.current) pakScanRef.current.value = ""; }
  };
  const updatePakItem = (id, field, val) => setPakItems((arr) => (arr || []).map((it) => (it._id === id ? { ...it, [field]: val } : it)));
  const removePakItem = (id) => setPakItems((arr) => (arr || []).filter((it) => it._id !== id));
  const confirmPakScan = async () => {
    const valid = (pakItems || []).filter((it) => String(it.naam).trim() && toNum(it.kg) > 0);
    if (!valid.length) return toast.error("Vul minstens één regel met naam en kg in.");
    if (!navigator.onLine) return toast.error("Toevoegen kan alleen online.");
    try {
      await saveMobileChanges((cd) => {
        let helpersArr = [...((cd.helpers || []).filter((h) => h && typeof h === "object"))];
        const inkopen = [];
        valid.forEach((it, i) => {
          const art = String(it.artikelnr || "").trim();
          let h = helpersArr.find((x) => (art && x.artikelnr && String(x.artikelnr).toLowerCase() === art.toLowerCase()) || x.naam.toLowerCase() === String(it.naam).trim().toLowerCase());
          if (!h) { h = { id: Date.now() + Math.random() + i, type: it.type || "Breader", naam: String(it.naam).trim(), prijs: toNum(it.prijs), artikelnr: art }; helpersArr = [...helpersArr, h]; }
          else if (art && !h.artikelnr) { helpersArr = helpersArr.map((x) => (x.id === h.id ? { ...x, artikelnr: art } : x)); }
          inkopen.push({ id: Date.now() + Math.random() + 1000 + i, datum: it.datum || today(), type: h.type, hulpstof: h.naam, leverancier: it.leverancier || "", kg: toNum(it.kg), prijs: toNum(it.prijs), batchnr: String(it.batchnr || ""), opmerking: it.pakbonnummer ? `Pakbon ${it.pakbonnummer}` : "" });
        });
        return { ...cd, helpers: helpersArr, hulpstofInkoop: [...inkopen, ...((cd.hulpstofInkoop || []).filter((b) => b && typeof b === "object"))] };
      });
      const n = valid.length;
      setPakItems(null);
      toast.success(`${n} hulpstof-binnenkomst(en) toegevoegd aan de voorraad.`);
      loadCloud();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };
  const onGrondPakbonScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!navigator.onLine) { toast.error("Scannen kan alleen online."); if (grondPakScanRef.current) grondPakScanRef.current.value = ""; return; }
    setGrondPakBusy(true);
    try {
      const all = []; let cnt = 0;
      for (const f of files) {
        let dataUrl; let mime = f.type || "application/pdf";
        if ((f.type || "").startsWith("image/")) { const blob = await compressImage(f, 2200, 0.85); dataUrl = await blobToDataUrl(blob); mime = "image/jpeg"; }
        else { dataUrl = await blobToDataUrl(f); }
        const res = await api.post("/ocr/pakbon-grondstof", { file_base64: dataUrl, mime_type: mime });
        const R = res.data?.result || {};
        const lev = String(R.leverancier || ""); const dat = (R.datum && /^\d{4}-\d{2}-\d{2}$/.test(R.datum) && R.datum <= today()) ? R.datum : today(); const pnr = String(R.pakbonnummer || "");
        (R.items || []).forEach((it) => {
          const naam = String(it.omschrijving || "").trim(); const art = String(it.artikelnummer || "").trim();
          const match = rawMaterials.find((m) => (art && m.artikelnummer && String(m.artikelnummer).toLowerCase() === art.toLowerCase()) || (naam && m.naam.toLowerCase() === naam.toLowerCase())) || (naam ? rawMaterials.find((m) => String(m.naam).toLowerCase() === bestMatchName(naam, rawMaterials.map((x) => x.naam)).toLowerCase()) : null);
          const scanLev = String(lev || "").trim(); const stamLev = String(match?.leverancier || "").trim();
          const storedArt = String(match?.artikelnummer || "").trim();
          const artChanged = !!(match && art && storedArt && art.toLowerCase() !== storedArt.toLowerCase());
          all.push({ _id: cnt++, leverancier: stamLev || scanLev, levScanned: scanLev, levStam: stamLev, levConflict: !!(stamLev && scanLev && stringSim(stamLev, scanLev) < 0.5), datum: dat, pakbonnummer: pnr, artikelnr: art || (match?.artikelnummer || ""), naam: match?.naam || naam, kg: toNum(it.kg), prijs: match ? toNum(match.prijs) : "", batchnr: String(it.batchnr || ""), bestaand: !!match, artChanged, oldArt: storedArt, updateArt: false });
        });
      }
      if (!all.length) { toast.error("Geen regels herkend."); return; }
      setGrondPakItems(all);
      toast.success(`${all.length} regel(s) uit ${files.length} pakbon(nen) herkend.`);
    } catch (err) { toast.error("OCR mislukt: " + formatApiError(err)); }
    finally { setGrondPakBusy(false); if (grondPakScanRef.current) grondPakScanRef.current.value = ""; }
  };
  const updateGrondPakItem = (id, field, val) => setGrondPakItems((arr) => (arr || []).map((it) => (it._id === id ? { ...it, [field]: val } : it)));
  const removeGrondPakItem = (id) => setGrondPakItems((arr) => (arr || []).filter((it) => it._id !== id));
  const mEigenTrace = (it) => { const lv = (data?.leveranciers || []).find((x) => x && x.naam === it.leverancier); const nr = lv && String(lv.nummer || "").trim() ? String(lv.nummer).trim() : ""; const code = String(it.batchnr || "").trim(); return code ? (nr ? `${code}-${nr}` : code) : ""; };
  const confirmGrondPakScan = async () => {
    const valid = (grondPakItems || []).filter((it) => String(it.naam).trim() && toNum(it.kg) > 0);
    if (!valid.length) return toast.error("Vul minstens één regel met naam en kg in.");
    if (!navigator.onLine) return toast.error("Toevoegen kan alleen online.");
    try {
      await saveMobileChanges((cd) => {
        let rawArr = [...((cd.rawMaterials || []).filter((m) => m && typeof m === "object"))];
        const levList = (cd.leveranciers || []).filter((x) => x && typeof x === "object");
        const eigenTr = (it) => { const lv = levList.find((x) => x.naam === it.leverancier); const nr = lv && String(lv.nummer || "").trim() ? String(lv.nummer).trim() : ""; const code = String(it.batchnr || "").trim(); return code ? (nr ? `${code}-${nr}` : code) : ""; };
        const batches = [];
        const traces = [];
        valid.forEach((it, i) => {
          const art = String(it.artikelnr || "").trim(); const naam = String(it.naam).trim();
          let m = rawArr.find((x) => (art && x.artikelnummer && String(x.artikelnummer).toLowerCase() === art.toLowerCase()) || x.naam.toLowerCase() === naam.toLowerCase());
          if (!m) { m = { id: Date.now() + Math.random() + i, naam, prijs: toNum(it.prijs), artikelnummer: art, leverancier: it.leverancier || "" }; rawArr = [...rawArr, m]; }
          else if (art && (!m.artikelnummer || (it.updateArt && String(m.artikelnummer).toLowerCase() !== art.toLowerCase()))) { rawArr = rawArr.map((x) => (x.id === m.id ? { ...x, artikelnummer: art } : x)); }
          const bid = Date.now() + 1000 + i;
          const levPartij = String(it.batchnr || "").trim();
          const eigen = (it.eigenTracecode != null && String(it.eigenTracecode).trim() !== "") ? String(it.eigenTracecode).trim() : eigenTr(it);
          batches.push({ id: bid, datum: it.datum || today(), grondstof: m.naam, bewerking: "Ontvangen", leverancier: it.leverancier || "", kg: toNum(it.kg), prijs: toNum(it.prijs), batchnr: levPartij, eigenTracecode: eigen, opmerking: it.pakbonnummer ? `Pakbon ${it.pakbonnummer}` : "" });
          if (levPartij || eigen) traces.push({ id: Date.now() + Math.random() + 5000 + i, datum: it.datum || today(), expeditieId: String(bid), grondstof: m.naam, leverancier: it.leverancier || "", levTracecode: levPartij, eigenTracecode: eigen, door: user?.name || user?.email || "Mobiel", ts: new Date().toISOString() });
        });
        return { ...cd, rawMaterials: rawArr, expeditie: [...batches, ...((cd.expeditie || []).filter((b) => b && typeof b === "object"))], traceLog: [...traces, ...((cd.traceLog || []).filter((t) => t && typeof t === "object"))] };
      });
      const n = valid.length;
      setGrondPakItems(null);
      toast.success(`${n} grondstof-binnenkomst(en) toegevoegd aan de voorraad.`);
      loadCloud();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const finalizeEntry = async (id) => {
    if (!navigator.onLine) return toast.error(t("offlinePlan"));
    const apply = (arr) => (arr || []).map((x) => (x && typeof x === "object" && x.id === id ? { ...x, status: "definitief" } : x));
    try {
      await saveMobileChanges((cd) => ({ ...cd, entries: apply(cd.entries) }));
      setData((d) => ({ ...d, entries: apply(d?.entries) }));
      toast.success(t("nuDefinitief"));
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const buildEntry = (hamKaasAllocatie = []) => ({
    id: Date.now() + Math.random(),
    datum: form.datum, lijn: form.lijn, product: form.product, artikelnummer: form.artikelnummer,
    geproduceerdKg: toNum(form.geproduceerdKg), uitvalKg: toNum(form.uitvalKg), uitvalReden: form.uitvalReden || "", goedProductKg: "",
    outputVerdeling: (Array.isArray(form.outputVerdeling) ? form.outputVerdeling : []).map((r) => ({ artikelnummer: String(r.artikelnummer || ""), kg: toNum(r.kg) })).filter((r) => r.kg > 0),
    aantalMensen: toNum(form.aantalMensen), productieUren: toNum(form.productieUren),
    stillstandUren: toNum(form.stillstandUren), normKgUur: toNum(form.normKgUur),
    grondstof: form.grondstof, halffabRunId: form.halffabRunId || "", halffabLots: (halffabNamenM.includes(form.grondstof) ? ((Array.isArray(form.halffabLots) && form.halffabLots.length) ? form.halffabLots : (form.halffabRunId ? [{ runId: form.halffabRunId, kg: Math.max(toNum(form.grondstofKg) - toNum(form.restantKg), 0) }] : [])).map((l) => ({ runId: String(l.runId || ""), kg: toNum(l.kg) })).filter((l) => l.runId && l.kg > 0) : []), grondstofKg: toNum(form.grondstofKg), restantKg: toNum(form.restantKg), prijsGrondstofKg: toNum(form.prijsGrondstofKg), kruidenKg: toNum(form.kruidenKg),
    predust: form.predust, predustKg: toNum(form.predustKg), predustPrijsKg: helperPrice("Predust", form.predust),
    battermix: form.battermix, battermixKg: toNum(form.battermixKg), battermixPrijsKg: helperPrice("Battermix", form.battermix),
    breader: form.breader, breaderKg: toNum(form.breaderKg), breaderPrijsKg: helperPrice("Breader", form.breader),
    hamkaas: form.hamkaas, hamkaasKg: toNum(form.hamkaasKg), hamkaasPrijsKg: helperPrice("Ham/kaas", form.hamkaas), hamkaasLots: hamKaasAllocatie, hamkaasBatchId: hamKaasAllocatie[0]?.batchId || "", hamkaasTrace: hamKaasAllocatie.map((lot) => lot.tracecode).join(", "),
    overigPerKg: overigStandaard(data.costs), opmerking: form.opmerking, photos: form.photos, status: mode,
    ingevoerdDoor: user?.name || user?.email || "",
    looptDoorFrituurolie: form.looptDoorFrituurolie === true,
  });

  const queueEntry = (entry) => { const q = [entry, ...loadQueue()]; saveQueue(q); setQueue(q); };

  const updatePlanStatus = async (planId, status, silent = false) => {
    if (!navigator.onLine) return toast.error(t("offlinePlan"));
    const apply = (arr) => (arr || []).map((p) => (p && typeof p === "object" && p.id === planId ? { ...p, status } : p));
    try {
      await saveMobileChanges((cd) => ({ ...cd, planning: apply(cd.planning) }));
      setData((d) => ({ ...d, planning: apply(d?.planning) }));
      if (!silent) toast.success(status === "gereed" ? t("planGereedTxt") : status === "gestart" ? t("planGestartTxt") : t("planOpenTxt"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const findHalffabForPlan = (p) => {
    if (p.halffabNaam && halffabNamenM.includes(p.halffabNaam)) return p.halffabNaam;
    if (halffabNamenM.includes(p.product)) return p.product;
    const rl = (data?.recepten || []).filter((r) => r && typeof r === "object");
    const r = rl.find((x) => (Array.isArray(x.eindproducten) && x.eindproducten.includes(p.product)) || x.eindproduct === p.product);
    if (r) { const hf = (r.ingredienten || []).find((i) => i.categorie === "Halffabricaat" && halffabNamenM.includes(i.naam)); if (hf) return hf.naam; }
    return "";
  };
  const startEnInvoeren = (p) => {
    const prod = products.find((x) => x.naam === p.product);
    const hf = findHalffabForPlan(p);
    setForm((f) => {
      const next = { ...f, datum: p.datum || f.datum, lijn: p.lijn || f.lijn, product: p.product || "", artikelnummer: p.artikelnummer || (prod?.artikelnummer || ""), normKgUur: prod?.normKgUur ?? f.normKgUur, opmerking: p.opmerking || f.opmerking, looptDoorFrituurolie: prod?.looptDoorFrituurolie === true };
      if (hf) { const vr = halffabVoorraadM(hf); next.grondstof = hf; next.prijsGrondstofKg = String(halffabKostprijsM(hf)); if (vr > 0) next.grondstofKg = String(Number(vr.toFixed(2))); }
      return next;
    });
    if (navigator.onLine && p.status !== "gereed" && p.status !== "gestart") updatePlanStatus(p.id, "gestart", true);
    setTimeout(() => invoerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    toast.success(`${p.product} ${hf ? t("invoerHfToast") : t("invoerToast")}`);
  };

  const save = async () => {
    if (isHalffabBookingBlocked(
      form.grondstof,
      form.artikelnummer,
      Math.max(toNum(form.grondstofKg) - toNum(form.restantKg), 0),
    )) {
      toast.error(halffabHoldMessage(form.grondstof));
      return;
    }
    if (!navigator.onLine) {
      toast.error("Productie boeken vereist een actuele cloudverbinding.");
      return;
    }
    if (loadQueue().length) {
      await flushQueue();
      if (loadQueue().length) {
        toast.error("Wacht tot alle eerdere tabletinvoer is gesynchroniseerd.");
        return;
      }
    }
    const knownCloudVersion = cloudVersionRef.current;
    const knownStoreVersion = cloudStoreVersionRef.current;
    const live = await loadCloud();
    if (!live) {
      toast.error("Geen verse cloudcontrole mogelijk. Er is niets geboekt.");
      return;
    }
    if (!knownCloudVersion || knownCloudVersion !== live.updatedAt || knownStoreVersion !== live.storeVersion) {
      toast.error("Voorraad is net bijgewerkt. Controleer de boeking opnieuw.");
      return;
    }
    if (mode !== "concept") {
      if (!form.lijn || !form.product) return toast.error(t("valLijnProduct"));
      if (form.geproduceerdKg === "") return toast.error(t("valGeproduceerd"));
      if (halffabNamenM.includes(form.grondstof)) { const lots = (Array.isArray(form.halffabLots) ? form.halffabLots : []).filter((l) => l.runId && toNum(l.kg) > 0); if (!lots.length) return toast.error("Kies minimaal één halffabricaat-lotnummer met kg."); }
    }
    let hamKaasAllocatie = [];
    if (form.hamkaas && toNum(form.hamkaasKg) > 0) {
      const opties = hamKaasFifoM(form.hamkaas);
      if (opties.length) {
        let nogTeBoeken = toNum(form.hamkaasKg);
        opties.forEach((batch) => {
          if (nogTeBoeken <= 0.001) return;
          const kg = Math.min(nogTeBoeken, batch.rest);
          if (kg > 0.001) {
            hamKaasAllocatie.push({ batchId: String(batch.id), kg, tracecode: batch.tracecode });
          }
          nogTeBoeken -= kg;
        });
        if (nogTeBoeken > 0.001) {
          return toast.error("Onvoldoende gesneden/invriesvoorraad. Registreer eerst een nieuwe invriespartij.");
        }
      }
    }
    setSaving(true);
    const entry = buildEntry(hamKaasAllocatie);
    for (const key of ["predust", "battermix", "breader", "hamkaas"]) {
      entry[`${key}StockLots`] = allocateHelperLots(live?.data || data || {}, entry[key], entry[`${key}Kg`]);
    }
    const summary = `${form.lijn} · ${form.product} · ${toNum(form.geproduceerdKg).toLocaleString("nl-NL")} kg · ${t("uitvalKort")} ${toNum(form.uitvalKg)} kg · ${form.photos.length} ${t("fotosWord")}`;
    try {
      if (navigator.onLine) {
        entry.photos = await resolvePhotos(entry.photos);
        await pushToCloud(entry, live.data, live.updatedAt);
        toast.success(t("savedOnline"), { description: summary });
        await loadCloud();
      } else {
        queueEntry(entry);
        toast.success(t("savedOffline"), { description: summary });
      }
      setForm(emptyForm());
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Boeking niet bevestigd door de cloud. Controleer de verbinding en probeer opnieuw.");
    } finally { setSaving(false); }
  };

  const install = async () => { if (!installEvt) return; installEvt.prompt(); const r = await installEvt.userChoice; if (r?.outcome === "accepted") setInstallEvt(null); };

  const inp = "mt-1 w-full rounded-lg border border-slate-300 px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:outline-none";
  const lbl = "text-sm font-semibold text-slate-700";

  return (
    <div className="min-h-screen bg-slate-100 pb-28" data-testid="mobiel-page">
      <ReleaseNoteViewer />
      <header className="sticky top-0 z-20 bg-[#1C64B0] text-white px-4 py-3 flex items-center justify-between shadow">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" alt="logo" className="h-8 w-8 rounded" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div>
            <div className="font-bold leading-none">{t("title")}</div>
            <div className="text-[11px] opacity-80">{user?.name} · {user?.role}</div>
            <div data-testid="app-version" className="text-[10px] opacity-70">Versie {APP_VERSION} · {APP_BUILD}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!online && <span data-testid="offline-badge" className="flex items-center gap-1 bg-amber-500 text-white text-[11px] font-bold px-2 py-1 rounded-full"><WifiOff className="h-3 w-3" /> {t("offline")}</span>}
          {queue.length > 0 && <span data-testid="queue-badge" className="flex items-center gap-1 bg-white/20 text-white text-[11px] font-bold px-2 py-1 rounded-full"><CloudUpload className="h-3 w-3" /> {queue.length}</span>}
          <button data-testid="device-badge" onClick={() => setAskDevice(true)} className="flex items-center gap-1 bg-white/15 text-white text-[11px] font-bold px-2 py-1 rounded-full max-w-[92px] truncate">{deviceName || "Toestel"}</button>
          <div className="flex items-center gap-1 bg-white/15 rounded-full pl-2 pr-1 py-0.5">
            <Globe className="h-3.5 w-3.5" />
            <select data-testid="lang-select" value={lang} onChange={(e) => changeLang(e.target.value)} className="bg-transparent text-white text-xs font-bold focus:outline-none">
              {LANGS.map((l) => <option key={l.code} value={l.code} className="text-slate-900">{l.label}</option>)}
            </select>
          </div>
          <button data-testid="mobiel-logout" onClick={async () => { await flushQueue(); if (loadQueue().length) return toast.error("Uitloggen gestopt: er wacht nog invoer op serverbevestiging."); logout(); navigate("/login"); }} className="p-2 rounded-md hover:bg-white/10"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>

      {(queue.length > 0 || queueError) && <div data-testid="mobile-pending-warning" role="alert" className="m-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        <p data-testid="mobile-queue-error">{queueError || `${queue.length} invoeractie(s) wachten nog op serverbevestiging. Nog niet alles is opgeslagen.`}</p>
        <button data-testid="mobile-retry-sync" className="mt-2 font-bold underline" onClick={() => flushQueue()}>Opnieuw synchroniseren</button>
      </div>}
      {askDevice && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="device-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5">
            <div className="font-bold text-slate-900 mb-1">Toestelnaam instellen</div>
            <p className="text-sm text-slate-500 mb-3">Geef dit toestel een naam (bijv. "Tablet Lijn 1"). Dit wordt getoond bij registraties en helpt bij het samenvoegen van offline-invoer.</p>
            <input data-testid="device-input" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base mb-3" placeholder="Tablet Lijn 1" value={deviceName} onChange={(e) => setDeviceName(e.target.value)} />
            <div className="flex gap-2">
              <button data-testid="device-skip" onClick={() => { try { localStorage.setItem("clazing_device_asked", "1"); } catch (e) { /* quota */ } setAskDevice(false); }} className="flex-1 rounded-xl border border-slate-300 py-2.5 font-bold text-slate-600">Overslaan</button>
              <button data-testid="device-save" onClick={() => { const n = deviceName.trim(); if (n) localStorage.setItem(DEVICE_KEY, n); try { localStorage.setItem("clazing_device_asked", "1"); } catch (e) { /* quota */ } setAskDevice(false); }} className="flex-1 rounded-xl bg-[#1C64B0] text-white py-2.5 font-bold">Opslaan</button>
            </div>
          </div>
        </div>
      )}

      {installEvt && (
        <button data-testid="install-btn" onClick={install} className="w-full bg-emerald-600 text-white font-semibold py-3 flex items-center justify-center gap-2">
          <Download className="h-4 w-4" /> {t("install")}
        </button>
      )}

      {!data && !online ? (
        <div className="p-6 text-center text-slate-500">{t("offlineNoData")}</div>
      ) : !data ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("loading")}</div>
      ) : (
        <div className="p-4 space-y-5 max-w-xl mx-auto">
          <div className="flex gap-1.5 bg-white rounded-xl p-1 shadow-sm overflow-x-auto" data-testid="m-screen-toggle">
            {[["planning", "Planning"], ["productie", "Productie"], ["registratie", "Registratie"], ["klok", "Klok"], ["inboeken", "Inboeken"], ["afboeken", "Afboeken"], ["bestellijst", "Bestellijst"], ["voorraad", "Voorraad"]].filter(([key]) => !allowedMobile || allowedMobile.includes(key)).map(([key, label]) => (
              <button key={key} data-testid={`m-screen-${key}`} onClick={() => setMobScreen(key)} className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-bold ${mobScreen === key ? "bg-[#1C64B0] text-white" : "text-slate-500"}`}>{label}</button>
            ))}
          </div>
          {mobScreen === "bestellijst" && (() => {
            const { grond, hulp } = computeStockList(data);
            const pallets = data?.bestellijst?.pallets || {};
            const typeOrder = ["Predust", "Battermix", "Breader", "Ham/kaas", "Marinade/Kruiden"];
            const hulpByType = typeOrder.map((tp) => ({ tp, items: hulp.filter((h) => h.type === tp) })).filter((g) => g.items.length);
            const overige = hulp.filter((h) => !typeOrder.includes(h.type));
            if (overige.length) hulpByType.push({ tp: "Overige hulpstoffen", items: overige });
            const sections = [{ tp: "Grondstoffen", items: grond }, ...hulpByType];
            const onChangeLocal = (key, v) => setData((d) => ({ ...d, bestellijst: { pallets: { ...(d?.bestellijst?.pallets || {}), [key]: v === "" ? undefined : toNum(v) }, leveranciers: d?.bestellijst?.leveranciers || {} } }));
            const pushPallet = async (key, raw) => { const val = raw === "" || raw == null ? undefined : toNum(raw); try { await saveMobileChanges((cd) => ({ ...cd, bestellijst: { pallets: { ...(cd.bestellijst?.pallets || {}), [key]: val }, leveranciers: cd.bestellijst?.leveranciers || {} } })); } catch { toast.error("Opslaan mislukt"); } };
            const visible = (items) => (orderFilter ? items.filter((it) => toNum(pallets[it.key]) > 0) : items);
            return (
              <div className="space-y-4" data-testid="m-bestellijst">
                <div className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between gap-3">
                  <div><div className="font-bold text-slate-800">Bestellijst</div><div className="text-xs text-slate-400">Vul per artikel het aantal pallets in. Besteldagen vul je later op de computer in.</div></div>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 whitespace-nowrap"><input type="checkbox" data-testid="m-order-filter" checked={orderFilter} onChange={(e) => setOrderFilter(e.target.checked)} /> alleen &gt;0</label>
                </div>
                {sections.map((sec) => visible(sec.items).length ? (
                  <section key={sec.tp} className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-xs font-bold uppercase tracking-widest text-[#1C64B0] mb-2">{sec.tp}</div>
                    <div className="divide-y divide-slate-100">
                      {visible(sec.items).map((it) => (
                        <div key={it.key} className="flex items-center gap-3 py-2" data-testid="m-best-row">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-800 truncate">{it.naam}</div>
                            <div className={`text-xs ${it.laag ? "text-rose-600 font-semibold" : "text-slate-400"}`}>voorraad {kgf(it.voorraad)}{it.min ? ` · min ${kgf(it.min)}` : ""}{it.leverancier ? ` · ${it.leverancier}` : ""}</div>
                          </div>
                          <input inputMode="numeric" type="number" min="0" step="1" data-testid="m-best-pallets" className="w-20 rounded-lg border border-slate-300 px-2 py-2 text-base text-center" placeholder="0" value={pallets[it.key] ?? ""} onChange={(e) => onChangeLocal(it.key, e.target.value)} onBlur={(e) => pushPallet(it.key, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null)}
                {sections.every((sec) => !visible(sec.items).length) && <div className="text-sm text-slate-400 bg-white rounded-xl p-4 text-center">Geen artikelen.</div>}
              </div>
            );
          })()}
          {mobScreen === "voorraad" && (() => {
            const { grond, hulp } = computeStockList(data);
            const runs = halffabRunsM;
            const ents = (data?.entries || []).filter((e) => e && typeof e === "object");
            const HT = ["Predust", "Battermix", "Breader", "Ham/kaas", "Marinade/Kruiden"];
            const isGrond = vTab === "grond";
            const chips = isGrond ? grond.map((g) => g.naam) : HT;
            const sel = vFilter && chips.includes(vFilter) ? vFilter : "";
            const items = isGrond ? (sel ? grond.filter((g) => g.naam === sel) : grond) : (sel ? hulp.filter((h) => h.type === sel) : hulp);
            const histOf = (naam) => isGrond ? grondstofAfboekingen(naam, ents, runs) : hulpstofAfboekingen(naam, ents, runs);
            return (
              <div className="space-y-4" data-testid="m-voorraad">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="font-bold text-slate-800 mb-1">Voorraadcontrole</div>
                  <div className="text-xs text-slate-400 mb-3">Tik een soort aan en zie hoe én wanneer de voorraad is afgeboekt.</div>
                  <div className="flex gap-2 mb-3">
                    <button data-testid="m-v-tab-grond" onClick={() => { setVTab("grond"); setVFilter(""); }} className={`flex-1 rounded-lg py-2 text-sm font-bold ${isGrond ? "bg-[#1C64B0] text-white" : "bg-slate-100 text-slate-500"}`}>Grondstoffen</button>
                    <button data-testid="m-v-tab-hulp" onClick={() => { setVTab("hulp"); setVFilter(""); }} className={`flex-1 rounded-lg py-2 text-sm font-bold ${!isGrond ? "bg-[#1C64B0] text-white" : "bg-slate-100 text-slate-500"}`}>Hulpstoffen</button>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="m-v-chips">
                    <button data-testid="m-v-chip-all" onClick={() => setVFilter("")} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold border ${sel === "" ? "bg-[#1C64B0] text-white border-[#1C64B0]" : "bg-white text-slate-600 border-slate-300"}`}>Alle</button>
                    {chips.map((c) => (<button key={c} data-testid={`m-v-chip-${c}`} onClick={() => setVFilter(c)} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold border ${sel === c ? "bg-[#1C64B0] text-white border-[#1C64B0]" : "bg-white text-slate-600 border-slate-300"}`}>{c}</button>))}
                  </div>
                </div>
                {items.length === 0 ? <div className="text-sm text-slate-400 bg-white rounded-xl p-4 text-center">Geen artikelen.</div> : items.map((it) => {
                  const h = histOf(it.naam);
                  return (
                    <section key={it.key || it.naam} className="bg-white rounded-xl p-4 shadow-sm" data-testid="m-v-item">
                      <div className="flex items-center justify-between mb-2">
                        <div><div className="font-semibold text-slate-800">{it.naam}</div>{!isGrond && <div className="text-xs text-slate-400">{it.type}</div>}</div>
                        <div className="text-right"><div className={`text-lg font-bold ${it.laag ? "text-rose-600" : "text-emerald-600"}`}>{kgf(it.voorraad)}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">voorraad nu</div></div>
                      </div>
                      {h.length === 0 ? <div className="text-xs text-slate-400">Nog geen afboekingen geregistreerd.</div> : (
                        <div className="divide-y divide-slate-100">
                          {h.slice(0, 30).map((a, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 text-sm" data-testid="m-v-hist-row">
                              <div className="min-w-0"><div className="truncate text-slate-700">{a.bron}</div><div className="text-[11px] text-slate-400">{a.datum}{a.detail ? ` \u00b7 ${a.detail}` : ""}</div></div>
                              <div className="font-semibold text-slate-700 whitespace-nowrap">{"\u2212"} {kgf(a.kg)}</div>
                            </div>
                          ))}
                          {h.length > 30 && <div className="pt-1 text-[11px] text-slate-400">+ {h.length - 30} eerdere afboekingen…</div>}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            );
          })()}
          {mobScreen === "planning" && (<>
          <section className="bg-white rounded-xl p-4 shadow-sm space-y-3" data-testid="m-planning">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><CalendarDays className="h-4 w-4" /> {t("planning")} · {t("weekAfk")} {getISOWeek(form.datum)}</div>
              <div className="flex gap-1">
                <button data-testid="m-plan-week" onClick={() => setPlanView("week")} className={`text-xs font-bold px-2 py-1 rounded-full ${planView === "week" ? "bg-[#1C64B0] text-white" : "bg-slate-100 text-slate-500"}`}>{t("planWeekBtn")}</button>
                <button data-testid="m-plan-dag" onClick={() => setPlanView("dag")} className={`text-xs font-bold px-2 py-1 rounded-full ${planView === "dag" ? "bg-[#1C64B0] text-white" : "bg-slate-100 text-slate-500"}`}>{t("planDagBtn")}</button>
              </div>
            </div>
            {planItems.length === 0 ? (
              <div className="text-sm text-slate-400" data-testid="m-plan-empty">{t("geenPlanning")}</div>
            ) : (
              <div className="space-y-3">
                {Object.entries(planByLine).map(([lijn, items]) => (
                  <div key={lijn} data-testid="m-plan-line">
                    <div className="text-sm font-bold text-slate-700 mb-1">{lijn}</div>
                    <div className="space-y-1">
                      {items.map((p) => (
                        <div key={p.id} data-testid="m-plan-row" className="bg-slate-50 rounded-lg px-3 py-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">{p.artikelnummer ? `${p.artikelnummer} - ${p.product}` : p.product}</div>
                              {p.opmerking && <div className="text-xs text-slate-500 truncate">{p.opmerking}</div>}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-sm font-bold text-[#1C64B0] tabular-nums">{toNum(p.hoeveelheid).toLocaleString("nl-NL")} {p.eenheid || "kg"}</div>
                              {planView === "week" && <div className="text-[11px] text-slate-400">{p.datum}</div>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span data-testid="m-plan-status" className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${p.status === "gereed" ? "bg-emerald-100 text-emerald-700" : p.status === "gestart" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"}`}>{p.status === "gereed" ? t("statusGereed") : p.status === "gestart" ? t("statusGestart") : t("statusOpen")}</span>
                            <div className="ml-auto flex gap-1">
                              <button data-testid="m-plan-start" onClick={() => updatePlanStatus(p.id, p.status === "gestart" ? "" : "gestart")} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${p.status === "gestart" ? "bg-amber-500 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>{t("planStart")}</button>
                              <button data-testid="m-plan-gereed" onClick={() => updatePlanStatus(p.id, p.status === "gereed" ? "" : "gereed")} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${p.status === "gereed" ? "bg-emerald-600 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>{t("planGereed")}</button>
                            </div>
                          </div>
                          <button data-testid="m-plan-invoeren" onClick={() => startEnInvoeren(p)} className="mt-2 w-full rounded-lg bg-[#1C64B0] hover:bg-[#154E8A] text-white text-sm font-bold py-2.5 flex items-center justify-center gap-2"><Play className="h-4 w-4" />{t("planInvoeren")}</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          </>)}
          {mobScreen === "productie" && (<>
          <section className="bg-white rounded-xl p-4 shadow-sm">
            <input ref={scanRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="m-scan-input" onChange={onScanForm} />
            <button data-testid="m-scan-btn" onClick={() => scanRef.current?.click()} disabled={scanning}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold py-4 disabled:opacity-60">
              {scanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />}
              {scanning ? t("scanBezig") : t("scanForm")}
            </button>
            <p className="text-xs text-slate-400 mt-2">{t("scanHint")}</p>
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm">
            <input ref={receptScanRef} type="file" accept="application/pdf,image/*" capture="environment" className="hidden" data-testid="m-recept-scan-input" onChange={onReceptScanM} />
            <button data-testid="m-recept-scan-btn" onClick={() => receptScanRef.current?.click()} disabled={receptScanning}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 disabled:opacity-60">
              {receptScanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />}
              {receptScanning ? t("scanBezig") : t("hfScanBtn")}
            </button>
            <p className="text-xs text-slate-400 mt-2">{t("hfScanHint")}</p>
          </section>
          {receptScan && (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-2" data-testid="m-recept-scan-modal">
              <div className="w-full max-w-lg max-h-[92vh] overflow-auto rounded-xl bg-white p-4">
                <div className="flex items-center justify-between mb-2"><h3 className="font-bold text-slate-900">{t("hfControleren")}</h3><button data-testid="m-recept-scan-close" disabled={receptSaving} onClick={() => setReceptScan(null)}><X className="h-5 w-5" /></button></div>
                <fieldset disabled={receptSaving || !!receptBookingRef.current} className="disabled:opacity-70">
                <div className="grid grid-cols-1 gap-2 mb-3">
                  <label className="text-xs font-semibold text-slate-500">{t("hfNaamLabel")}<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="m-recept-scan-naam" value={receptScan.naam} onChange={(e) => setReceptScan((s) => ({ ...s, naam: e.target.value }))} /></label>
                  <div className="grid grid-cols-3 gap-2">
                    <label className="text-xs font-semibold text-slate-500">Datum<input type="date" data-testid="m-recept-scan-datum" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={receptScan.datum} onChange={(e) => setReceptScan((s) => ({ ...s, datum: e.target.value }))} /></label>
                    <label className="text-xs font-semibold text-slate-500">{t("hfRondjes")}<input type="number" step="any" placeholder="5" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="m-recept-scan-rondjes" value={receptScan.rondjes} onChange={(e) => setReceptScan((s) => ({ ...s, rondjes: e.target.value }))} /></label>
                    <label className="text-xs font-semibold text-slate-500">{t("hfBeginGewicht")}<input type="number" step="any" placeholder="1500" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="m-recept-scan-hoofdkg" value={receptScan.hoofdKg} onChange={(e) => setReceptScan((s) => ({ ...s, hoofdKg: e.target.value }))} /></label>
                  </div>
                </div>
                {openPrepPlanning.length > 0 && (
                  <label className="text-xs font-semibold text-slate-500 block mb-3">{t("hfKoppelPlan")}
                    <select data-testid="m-recept-scan-plan" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={receptScan.planId || ""} onChange={(e) => setReceptScan((s) => ({ ...s, planId: e.target.value }))}>
                      <option value="">{t("hfNietKoppelen")}</option>
                      {openPrepPlanning.map((p) => <option key={p.id} value={String(p.id)}>{p.datum} · {p.lijn} · {p.product} · {p.hoeveelheid} {p.eenheid || "kg"}</option>)}
                    </select>
                  </label>
                )}
                {!receptScan.planId && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <label className="text-xs font-semibold text-slate-500">{t("hfInplannenLijn")}
                      <select data-testid="m-recept-scan-planlijn" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={receptScan.planLijn || ""} onChange={(e) => setReceptScan((s) => ({ ...s, planLijn: e.target.value }))}>
                        <option value="">{t("hfNietInplannen")}</option>
                        {lines.filter((l) => !/gaarlijn/i.test(String(l.naam || ""))).map((l) => <option key={l.id} value={l.naam}>{l.naam}</option>)}
                      </select>
                    </label>
                    <label className="text-xs font-semibold text-slate-500">{t("hfDag")}
                      <input type="date" data-testid="m-recept-scan-plandatum" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={receptScan.planDatum || receptScan.datum} onChange={(e) => setReceptScan((s) => ({ ...s, planDatum: e.target.value }))} />
                    </label>
                  </div>
                )}
                <p className="mb-2 text-xs font-medium text-emerald-700">{t("hfVariantHint")}</p>
                <datalist id="m-grond-datalist">{grondNamenM.map((n) => <option key={n} value={n} />)}</datalist>
                <div className="space-y-2">
                  {receptScan.lines.map((it) => (
                    <div key={it.id} className="rounded-lg border border-slate-200 p-2" data-testid={`m-recept-line-${it.id}`}>
                      <div className="flex items-center gap-2">
                        <input list="m-grond-datalist" data-testid={`m-recept-naam-${it.id}`} className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" value={it.naam} onChange={(e) => setLineNaamM(it.id, e.target.value)} />
                        <button data-testid={`m-recept-remove-${it.id}`} onClick={() => setReceptScan((s) => ({ ...s, lines: s.lines.filter((x) => x.id !== it.id) }))} className="text-rose-600"><X className="h-4 w-4" /></button>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        <select data-testid={`m-recept-categorie-${it.id}`} className="rounded border border-slate-200 px-2 py-1 text-sm" value={it.categorie} onChange={(e) => setReceptScan((s) => ({ ...s, lines: s.lines.map((x) => (x.id === it.id ? { ...x, categorie: e.target.value } : x)) }))}><option value="Grondstof">{t("catGrondstof")}</option><option value="Hulpstof">{t("catHulpstof")}</option><option value="Halffabricaat">{t("catHalffabricaat")}</option></select>
                        <input type="number" data-testid={`m-recept-kg-${it.id}`} placeholder="kg" className="rounded border border-slate-200 px-2 py-1 text-sm" value={it.kg} onChange={(e) => setReceptScan((s) => ({ ...s, lines: s.lines.map((x) => (x.id === it.id ? { ...x, kg: e.target.value } : x)) }))} />
                        <input type="number" data-testid={`m-recept-prijs-${it.id}`} placeholder={"\u20ac/kg"} className="rounded border border-slate-200 px-2 py-1 text-sm" value={it.prijs} onChange={(e) => setReceptScan((s) => ({ ...s, lines: s.lines.map((x) => (x.id === it.id ? { ...x, prijs: e.target.value } : x)) }))} />
                      </div>
                      {(it.categorie || "Grondstof") === "Grondstof" && String(it.naam || "").trim() && (() => {
                        const lots = receptBatchLotsM(it);
                        return (
                          <div className="mt-2 space-y-1.5 rounded border border-blue-200 bg-blue-50 p-2" data-testid={`m-recept-batchlots-${it.id}`}>
                            <div className="text-[11px] font-semibold text-blue-800">Partijen / tracecodes</div>
                            {lots.length === 0 ? (
                              <select
                                data-testid={`m-recept-batch-${it.id}-0`}
                                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                                value=""
                                onChange={(event) => {
                                  if (event.target.value) setLineBatchM(it.id, 0, event.target.value);
                                }}
                              >
                                <option value="">— partij/tracecode kiezen —</option>
                                {grondBatchOptiesM(it.naam).filter((option) => option.rest > 0.001).map((option) => (
                                  <option key={option.id} value={option.id}>
                                    {(option.batchnr || option.eigenTracecode || "partij")}
                                    {` · rest ${Math.round(Math.max(option.rest, 0))}kg`}
                                  </option>
                                ))}
                              </select>
                            ) : lots.map((lot, lotIndex) => {
                              const opties = grondBatchOptiesM(it.naam).filter((option) => (
                                option.rest > 0.001 || String(option.id) === String(lot.expeditieId)
                              ));
                              return (
                                <div key={`${it.id}-${lotIndex}`} className="flex items-center gap-1">
                                  <select
                                    data-testid={`m-recept-batch-${it.id}-${lotIndex}`}
                                    className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                                    value={lot.expeditieId || ""}
                                    onChange={(event) => setLineBatchM(it.id, lotIndex, event.target.value)}
                                  >
                                    <option value="">— partij —</option>
                                    {opties.map((option) => (
                                      <option key={option.id} value={option.id}>
                                        {(option.batchnr || option.eigenTracecode || "partij")}
                                        {` · rest ${Math.round(Math.max(option.rest, 0))}kg`}
                                      </option>
                                    ))}
                                  </select>
                                  <input
                                    type="number"
                                    step="any"
                                    data-testid={`m-recept-batchkg-${it.id}-${lotIndex}`}
                                    className="w-20 rounded border border-slate-300 px-2 py-1 text-sm"
                                    value={lot.kg}
                                    onChange={(event) => setLineBatchLotKgM(
                                      it.id,
                                      lotIndex,
                                      event.target.value,
                                    )}
                                  />
                                  {lots.length > 1 && (
                                    <button
                                      type="button"
                                      data-testid={`m-recept-batchremove-${it.id}-${lotIndex}`}
                                      className="px-1 text-rose-600"
                                      onClick={() => removeLineBatchLotM(it.id, lotIndex)}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                            <div className="flex items-center justify-between gap-2 text-[11px]">
                              <button
                                type="button"
                                data-testid={`m-recept-batch-add-${it.id}`}
                                className="font-semibold text-blue-700"
                                onClick={() => addLineBatchLotM(it.id)}
                              >
                                + partij splitsen
                              </button>
                              <span className={Math.abs(receptLotKgM(it) - toNum(it.kg)) > 0.01 ? "font-semibold text-amber-700" : "text-emerald-700"}>
                                {kgf(receptLotKgM(it))} / {kgf(toNum(it.kg))}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                      <input placeholder={t("phPartijLot")} className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-sm" data-testid={`m-recept-partij-${it.id}`} value={it.partijnummer || ""} onChange={(e) => linkPartijM(it.id, e.target.value)} />
                    </div>
                  ))}
                </div>
                <button data-testid="m-recept-add-grond" onClick={addGrondLineM} className="mt-2 w-full rounded-lg border-2 border-dashed border-[#1C64B0]/40 text-[#1C64B0] text-sm font-semibold py-2 hover:bg-[#1C64B0]/5">+ {t("hfAddGrond")}</button>
                {(() => {
                  const norm = (x) => String(x || "").toLowerCase().replace(/\s+/g, " ").trim();
                  const rmNames = rawMaterials.map((m) => m.naam);
                  const normMap = new Map(rmNames.map((n) => [norm(n), n]));
                  const problems = (receptScan.lines || [])
                    .filter((l) => (l.categorie || "Grondstof") === "Grondstof" && String(l.naam || "").trim())
                    .map((l) => { const canon = normMap.get(norm(l.naam)); if (canon && canon === l.naam) return null; if (canon) return { l, suggest: canon, exact: true }; const bm = bestMatchName(l.naam, rmNames); const sim = bm ? stringSim(l.naam, bm) : 0; return { l, suggest: sim >= 0.4 ? bm : "", exact: false }; })
                    .filter(Boolean);
                  if (!problems.length) return null;
                  const setNaam = (id, nn) => setReceptScan((s) => ({ ...s, lines: s.lines.map((x) => (x.id === id ? { ...x, naam: nn, categorie: "Grondstof" } : x)) }));
                  return (
                    <div data-testid="m-recept-koppel" className="mt-3 space-y-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-xs text-rose-800">
                      <div className="font-semibold">{t("hfKoppelTitle")}</div>
                      {problems.map(({ l, suggest, exact }) => (
                        <div key={l.id} className="space-y-1 border-t border-rose-200 pt-1.5 first:border-t-0 first:pt-0">
                          <div className="font-mono">"{l.naam}"</div>
                          <select data-testid={`m-recept-koppel-sel-${l.id}`} className="w-full rounded border border-rose-300 px-2 py-1.5" value="" onChange={(e) => { if (e.target.value) setNaam(l.id, e.target.value); }}>
                            <option value="">{t("hfKoppelKies")}</option>
                            {rmNames.slice().sort((a, b) => String(a).localeCompare(String(b))).map((n) => <option key={n} value={n}>{n}</option>)}
                          </select>
                          {suggest && <button data-testid={`m-recept-koppel-apply-${l.id}`} onClick={() => setNaam(l.id, suggest)} className="w-full rounded-md bg-rose-600 px-2.5 py-1.5 font-semibold text-white">{exact ? `${t("hfKoppelCorrigeer")} "${suggest}"` : `${t("hfKoppelBedoel")} "${suggest}"?`}</button>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                </fieldset>
                {receptSaveError && <p role="alert" data-testid="m-recept-save-error" className="mt-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">{receptSaveError}</p>}
                {(() => { const sc = scanScaleM(receptScan); const b = receptScan.lines.reduce((s, l) => s + toNum(l.kg), 0) * sc; const tk = receptScan.lines.reduce((s, l) => s + toNum(l.kg) * toNum(l.prijs), 0) * sc; return (
                  <div className="mt-3">
                    <div className="text-sm text-slate-600 mb-2">×{sc.toLocaleString("nl-NL", { maximumFractionDigits: 3 })} · {t("woTotaal")} {b.toLocaleString("nl-NL", { maximumFractionDigits: 1 })} kg · {t("woKostprijs")} <b className="text-[#1C64B0]" data-testid="m-recept-scan-kostprijs">€{(tk / (b || 1)).toFixed(2)}/kg</b></div>
                    <div className="flex gap-2"><button data-testid="m-recept-scan-cancel" disabled={receptSaving} onClick={() => setReceptScan(null)} className="flex-1 rounded-lg border border-slate-300 py-3 font-semibold text-slate-600">{receptBookingRef.current ? "Sluiten" : t("annuleren")}</button><button data-testid="m-recept-scan-confirm" disabled={receptSaving} onClick={confirmReceptScan} className="flex-1 rounded-lg bg-[#1C64B0] text-white py-3 font-semibold disabled:opacity-60">{receptSaving ? "Opslaan en controleren…" : receptSaveError ? "Opnieuw proberen" : t("boeken")}</button></div>
                  </div>
                ); })()}
              </div>
            </div>
          )}

          <section ref={invoerRef} className="bg-white rounded-xl p-4 shadow-sm space-y-4">
            <div
              data-testid="m-productie-sync-status"
              className={`rounded-lg border px-3 py-2 text-xs font-semibold ${
                productieSynced
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-300 bg-amber-50 text-amber-800"
              }`}
            >
              {productieSynced
                ? "Cloudvoorraad gecontroleerd. Nieuwe boeking mag door."
                : "Cloudvoorraad wordt gecontroleerd. Boeken blijft geblokkeerd tot deze vers is."}
            </div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{t("basis")}</div>
            <div><label className={lbl}>{t("datum")}</label><input type="date" data-testid="m-datum" className={inp + hl("datum")} value={form.datum} onChange={(e) => set("datum", e.target.value)} /></div>
            <div><label className={lbl}>{t("lijn")}</label>
              <select data-testid="m-lijn" className={inp + hl("lijn")} value={form.lijn} onChange={(e) => set("lijn", e.target.value)}>
                <option value="">{t("kiesLijn")}</option>
                {lines.map((l) => <option key={l.id} value={l.naam}>{l.naam}</option>)}
              </select>
            </div>
            <div><label className={lbl}>{t("product")}</label>
              <ProductPicker testid="m-product" products={products} value={form.product} onChange={(v) => set("product", v)} />
            </div>
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm space-y-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{t("secGrondVlees")}</div>
            <div><label className={lbl}>{t("grondstofLabel")}</label>
              <select data-testid="m-grondstof" className={inp} value={form.grondstof} onChange={(e) => onGrondstofM(e.target.value)}>
                <option value="">—</option>
                {rawMaterials.map((m) => <option key={m.id} value={m.naam}>{m.naam}</option>)}
                {halffabNamenM.filter((n) => halffabVoorraadM(n) > 0.001 || n === form.grondstof).map((n) => <option key={n} value={n}>⬡ {n} {t("lblHalffabNog")} {kgf(Math.max(halffabVoorraadM(n), 0))}</option>)}
              </select>
            </div>
            {halffabNamenM.includes(form.grondstof) ? (() => {
              const opts = halffabRunOptiesM(form.grondstof);
              const lots = (form.halffabLots && form.halffabLots.length) ? form.halffabLots : (form.halffabRunId ? [{ runId: form.halffabRunId, kg: String(Math.max(toNum(form.grondstofKg) - toNum(form.restantKg), 0)) }] : [{ runId: "", kg: "" }]);
              const totaal = lots.reduce((s, l) => s + toNum(l.kg), 0);
              const setLot = (i, field, val) => updateHalffabLotsM(lots.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
              const addLot = () => updateHalffabLotsM([...lots, { runId: "", kg: "" }]);
              const removeLot = (i) => { const nx = lots.filter((_, idx) => idx !== i); updateHalffabLotsM(nx.length ? nx : [{ runId: "", kg: "" }]); };
              return (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2" data-testid="m-halffab-lots">
                  <div className="text-xs font-bold text-blue-700">Halffabricaat-lotnummer(s) — splits indien nodig over meerdere partijen</div>
                  {lots.map((l, i) => { const avail = opts.filter((o) => o.rest > 0.001 || String(o.id) === String(l.runId) || lots.some((x) => String(x.runId) === String(o.id))); return (
                    <div key={i} className="flex gap-2 items-center">
                      <select data-testid={`m-halffab-lot-${i}`} className={`${inp} flex-1`} value={l.runId || ""} onChange={(e) => setLot(i, "runId", e.target.value)}>
                        <option value="">{avail.length ? "— kies lotnummer —" : "— geen lotnummers —"}</option>
                        {avail.map((o) => <option key={o.id} value={o.id}>{o.lotcode} · rest {kgf(Math.max(o.rest, 0))}</option>)}
                      </select>
                      <input inputMode="decimal" data-testid={`m-halffab-lotkg-${i}`} className={`${inp} w-24`} placeholder="kg" value={l.kg} onChange={(e) => setLot(i, "kg", e.target.value)} />
                      {lots.length > 1 && <button type="button" data-testid={`m-halffab-lot-remove-${i}`} onClick={() => removeLot(i)} className="text-rose-600 font-bold px-2">✕</button>}
                    </div>
                  ); })}
                  <div className="flex items-center justify-between">
                    <button type="button" data-testid="m-halffab-lot-add" onClick={addLot} className="text-sm font-semibold text-blue-700">+ lotnummer toevoegen</button>
                    <div className="text-sm text-slate-600">Totaal: <b>{kgf(totaal)}</b></div>
                  </div>
                </div>
              );
            })() : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>{t("ingezetKg")}</label><input inputMode="decimal" data-testid="m-grondstof-kg" className={inp} value={form.grondstofKg} onChange={(e) => set("grondstofKg", e.target.value)} /></div>
                  <div><label className={lbl}>{t("restantKgLbl")}</label><input inputMode="decimal" data-testid="m-restant-kg" className={inp} value={form.restantKg} onChange={(e) => set("restantKg", e.target.value)} /></div>
                </div>
                {toNum(form.restantKg) > 0 && <p className="text-xs text-slate-500" data-testid="m-restant-note">{t("restantNote1")} <b>{Math.max(toNum(form.grondstofKg) - toNum(form.restantKg), 0).toLocaleString("nl-NL", { maximumFractionDigits: 1 })}</b> {t("restantNote2")} {toNum(form.restantKg)} {t("restantNote3")}</p>}
              </>
            )}
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm space-y-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{t("productieUitval")}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>{t("geproduceerd")}</label>{(form.outputVerdeling && form.outputVerdeling.length) ? <div data-testid="m-geproduceerd-totaal" className={`${inp} bg-slate-100 text-slate-700`}>{kgf(toNum(form.geproduceerdKg))}</div> : <input inputMode="decimal" data-testid="m-geproduceerd" className={inp + hl("geproduceerdKg")} value={form.geproduceerdKg} onChange={(e) => set("geproduceerdKg", e.target.value)} />}</div>
              <div><label className={lbl}>{t("uitval")}</label><input inputMode="decimal" data-testid="m-uitval" className={inp + hl("uitvalKg")} value={form.uitvalKg} onChange={(e) => set("uitvalKg", e.target.value)} /></div>
              <div className="col-span-2"><label className={lbl}>Reden uitval</label><input data-testid="m-uitvalreden" className={inp} value={form.uitvalReden} onChange={(e) => set("uitvalReden", e.target.value)} placeholder="bijv. aanloop, verbrand, misvorming" /></div>
              <div><label className={lbl}>{t("mensen")}</label><input inputMode="decimal" className={inp + hl("aantalMensen")} value={form.aantalMensen} onChange={(e) => set("aantalMensen", e.target.value)} /></div>
              <div><label className={lbl}>{t("uren")}</label><input inputMode="decimal" className={inp + hl("productieUren")} value={form.productieUren} onChange={(e) => set("productieUren", e.target.value)} /></div>
              <div><label className={lbl}>{t("stilstand")}</label><input inputMode="decimal" className={inp + hl("stillstandUren")} value={form.stillstandUren} onChange={(e) => set("stillstandUren", e.target.value)} /></div>
            </div>
            {(() => {
              const rows = form.outputVerdeling || [];
              const tot = rows.reduce((s, r) => s + toNum(r.kg), 0);
              const setRow = (i, field, val) => updateOutputVerdelingM(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
              const addRow = () => updateOutputVerdelingM([...rows, { artikelnummer: "", kg: "" }]);
              const removeRow = (i) => updateOutputVerdelingM(rows.filter((_, idx) => idx !== i));
              return rows.length ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="m-output-verdeling">
                  <div className="text-xs font-bold text-emerald-700">Verdeel eindgewicht over artikelnummers/klanten — totaal telt automatisch</div>
                  {rows.map((r, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input className={`${inp} flex-1`} data-testid={`m-verdeling-art-${i}`} value={r.artikelnummer} onChange={(e) => setRow(i, "artikelnummer", e.target.value)} placeholder="artikelnr / klant" />
                      <input inputMode="decimal" className={`${inp} w-24`} data-testid={`m-verdeling-kg-${i}`} value={r.kg} onChange={(e) => setRow(i, "kg", e.target.value)} placeholder="kg" />
                      <button type="button" data-testid={`m-verdeling-remove-${i}`} onClick={() => removeRow(i)} className="text-rose-600 font-bold px-2">✕</button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between">
                    <button type="button" data-testid="m-verdeling-add" onClick={addRow} className="text-sm font-semibold text-emerald-700">+ artikelnummer</button>
                    <div className="text-sm text-slate-600">Totaal: <b>{kgf(tot)}</b></div>
                  </div>
                </div>
              ) : (
                <button type="button" data-testid="m-verdeling-start" onClick={addRow} className="text-sm font-semibold text-emerald-700">+ Verdeel over meerdere artikelnummers/klanten</button>
              );
            })()}
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm space-y-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{t("coating")}</div>
            {[["Predust", "predust", "predustKg"], ["Battermix", "battermix", "battermixKg"], ["Breader", "breader", "breaderKg"], ["Ham/kaas", "hamkaas", "hamkaasKg"]].map(([type, nameKey, kgKey]) => (
              <div key={type} className="grid grid-cols-2 gap-3 items-end">
                <div><label className={lbl}>{type}</label>
                  <select
                    data-testid={`m-${nameKey}`}
                    className={inp}
                    value={form[nameKey]}
                    onChange={(e) => set(nameKey, e.target.value)}
                  >
                    <option value="">—</option>
                    {helperNames(type).map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div><label className={lbl}>{t("kg")}</label><input inputMode="decimal" className={inp + hl(kgKey)} value={form[kgKey]} onChange={(e) => set(kgKey, e.target.value)} /></div>
              </div>
            ))}
            {form.hamkaas && hamKaasFifoM(form.hamkaas).length > 0 && (
              <div
                className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-xs text-cyan-950"
                data-testid="m-hamkaas-fifo"
              >
                <b>Gesneden/invriesvoorraad:</b> bij opslaan wordt FIFO afgeboekt vanaf
                {" "}<b>{hamKaasFifoM(form.hamkaas)[0].tracecode}</b>
                {" "}({kgf(hamKaasFifoM(form.hamkaas)[0].rest)} beschikbaar).
              </div>
            )}
            <p className="text-xs text-slate-400">{t("coatingNote")}</p>
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm space-y-3">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{t("fotos")}</div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="m-photo-input" onChange={onPickPhotos} />
            <button data-testid="m-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 text-blue-700 font-semibold py-4">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
              {uploading ? t("bezig") : t("fotoToevoegen")}
            </button>
            {form.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="m-photo-previews">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={photoSrc(p)} alt="" className="w-full h-24 object-cover rounded-md border" />
                    {typeof p !== "string" && <span className="absolute bottom-1 left-1 bg-amber-500 text-white text-[9px] font-bold px-1 rounded">{t("wachtSync")}</span>}
                    <button onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full p-1"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm">
            <label className={lbl}>{t("opmerking")}</label>
            <textarea data-testid="m-opmerking" rows={2} className={inp + hl("opmerking")} value={form.opmerking} onChange={(e) => set("opmerking", e.target.value)} placeholder={t("opmerkingPlaceholder")} />
          </section>

          <section>
            <div className="flex items-center gap-2 text-slate-500 text-sm font-semibold mb-2"><ClipboardList className="h-4 w-4" /> {t("laatst")}</div>
            <div className="space-y-2" data-testid="m-recent">
              {recent.length === 0 ? <div className="text-sm text-slate-400 bg-white rounded-lg p-3">{t("nietsIngevoerd")}</div> : recent.map((r, idx) => (
                <div key={r.id || idx} className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold text-slate-800">{r.product}</span>
                    <span className="text-slate-400 flex items-center gap-1">{r._pending && <CloudUpload className="h-3 w-3 text-amber-500" />}{r.datum}</span>
                  </div>
                  <div className="text-xs text-slate-500">{r.lijn} · {toNum(r.geproduceerdKg)} kg · {t("uitvalKort")} {toNum(r.uitvalKg)} kg{r._pending ? ` · ${t("wachtSynchronisatie")}` : ""}</div>
                  {Array.isArray(r.photos) && r.photos.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {r.photos.slice(0, 4).map((p, i) => <img key={i} src={photoSrc(p)} alt="" className="h-12 w-12 object-cover rounded border" />)}
                      {r.photos.length > 4 && <span className="text-xs text-slate-400 self-end">+{r.photos.length - 4}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
          </>)}
          {mobScreen === "inboeken" && (<>
          <section className="bg-white rounded-xl p-4 shadow-sm">
            <input ref={pakScanRef} type="file" accept="application/pdf,image/*" multiple className="hidden" data-testid="m-pakbon-input" onChange={onPakbonScan} />
            <button data-testid="m-pakbon-btn" onClick={() => pakScanRef.current?.click()} disabled={pakBusy}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 disabled:opacity-60">
              {pakBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />}
              {pakBusy ? t("scanBezigKort") : t("pakHulpBtn")}
            </button>
            <p className="text-xs text-slate-400 mt-2">{t("pakHulpHint")}</p>
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm">
            <input ref={grondPakScanRef} type="file" accept="application/pdf,image/*" multiple className="hidden" data-testid="m-grond-pakbon-input" onChange={onGrondPakbonScan} />
            <button data-testid="m-grond-pakbon-btn" onClick={() => grondPakScanRef.current?.click()} disabled={grondPakBusy}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 disabled:opacity-60">
              {grondPakBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />}
              {grondPakBusy ? t("scanBezigKort") : t("pakGrondBtn")}
            </button>
            <p className="text-xs text-slate-400 mt-2">{t("pakGrondHint")}</p>
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm">
            <input ref={divPakScanRef} type="file" accept="application/pdf,image/*" multiple className="hidden" data-testid="m-diversen-pakbon-input" onChange={onDivPakbonScan} />
            <button data-testid="m-diversen-pakbon-btn" onClick={() => divPakScanRef.current?.click()} disabled={divPakBusy}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 disabled:opacity-60">
              {divPakBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />}
              {divPakBusy ? t("scanBezigKort") : "Scan pakbon diversen (dozen, folie)"}
            </button>
            <p className="text-xs text-slate-400 mt-2">Fotografeer of upload een pakbon van dozen, folie of schoonmaak. Na controle worden de aantallen ingeboekt.</p>
          </section>

          <section className="bg-white rounded-xl p-4 shadow-sm">
            <button data-testid="m-diversen-open-btn" onClick={() => setDivOpen(true)}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4">
              <ClipboardList className="h-5 w-5" /> Diversen handmatig inboeken
            </button>
            <p className="text-xs text-slate-400 mt-2">Boek ontvangen dozen, folie, schoonmaak &amp; PBM handmatig in de voorraad.</p>
          </section>
          </>)}
          {mobScreen === "afboeken" && (<>
          <section className="bg-white rounded-xl p-4 shadow-sm">
            <div className="font-bold text-slate-800 mb-1">Diversen afboeken</div>
            <div className="text-xs text-slate-400 mb-3">Boek verbruikte dozen/folie af zodra ze in productie zijn gezet. Alleen artikelen met "inboeken" aangevinkt (en niet op vast) verschijnen hier.</div>
            <input data-testid="m-divafb-search" className={inp + " mb-3"} placeholder="Zoek doos, folie, artikelnr…" value={divAfbSearch} onChange={(e) => setDivAfbSearch(e.target.value)} />
            <div className="space-y-2">
              {divAfbList.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6" data-testid="m-divafb-empty">Geen artikelen. Vink in Stamgegevens (diversen) de kolom "inboeken" aan om een artikel hier te tonen.</div>
              ) : divAfbList.map((m) => (
                <div key={m.id} data-testid="m-divafb-row" className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{m.naam}</div>
                      <div className="text-[11px] text-slate-400 truncate">{m.artikelnummer ? m.artikelnummer + " · " : ""}{m.categorie} · {m.eenheid} · voorraad {fmtAant(divStock(m))}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <input inputMode="decimal" type="number" min="0" step="any" data-testid={`m-divafb-qty-${m.id}`} className="flex-1 rounded-lg border border-rose-200 px-3 py-2.5 text-base" placeholder={`Verbruik (${m.eenheid || ""})`} value={divAfbQty[m.id] ?? ""} onChange={(e) => setDivAfbQty({ ...divAfbQty, [m.id]: e.target.value })} />
                    <button data-testid={`m-divafb-btn-${m.id}`} disabled={divBusy} onClick={() => afboekDiversenM(m)} className="rounded-lg bg-rose-600 text-white font-semibold px-4 py-2.5 text-sm shrink-0 disabled:opacity-60">Afboek</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          </>)}
          {mobScreen === "klok" && <KlokPaneel />}
          {mobScreen === "registratie" && (<>
            {regView === "lijst" && (<>
              {(() => {
                const miss = ontbrekendeRegistraties(data, regDatum);
                const thr = data?.regFormConfig?.waarschuwingUur ?? 16;
                const isToday = regDatum === today();
                const show = miss.length > 0 && (!isToday || new Date().getHours() >= thr) && regWarnHiddenDate !== regDatum;
                if (!show) return null;
                return (
                  <section data-testid="m-reg-missing" className="bg-rose-50 border border-rose-300 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-bold text-rose-800 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Nog niet afgerond ({miss.length})</div>
                      <button data-testid="m-reg-missing-dismiss" onClick={() => { setRegWarnHiddenDate(regDatum); localStorage.setItem("clazing_mreg_warn_hidden", regDatum); }} className="text-rose-500"><X className="h-5 w-5" /></button>
                    </div>
                    <ul className="space-y-1 text-sm text-rose-900">
                      {miss.map((m, i) => <li key={i}><b>{m.tpl.code}</b> {m.tpl.titel}{m.lijn ? ` — ${m.lijn}` : ""}{m.tpl.periode === "week" ? " (deze week)" : ""}</li>)}
                    </ul>
                  </section>
                );
              })()}
              {(() => {
                const taken = openKalibratieTaken(data).filter((t) => kalibratieZichtbaarVoor(t.afdeling, user?.role, user?.roleBase));
                if (!taken.length) return null;
                return (
                  <section data-testid="m-kal-taken" className="bg-amber-50 border border-amber-300 rounded-xl p-4">
                    <div className="font-bold text-amber-900 flex items-center gap-2 mb-2"><ClipboardCheck className="h-5 w-5" /> Kalibratietaken ({taken.length})</div>
                    <div className="space-y-2">
                      {taken.map((t) => (
                        <button key={t.id} data-testid={`m-kal-taak-${t.id}`} onClick={() => openKalibratieTaak(t)} className="w-full text-left bg-white rounded-lg p-3 shadow-sm flex items-center justify-between gap-2">
                          <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{t.tpl.code} · {t.tpl.titel}</div><div className="text-[11px] text-slate-500 truncate">{t.grondstof}{t.batchCode ? ` · ${t.batchCode}` : ""}{t.datum ? ` · ${t.datum}` : ""}{t.afdeling ? ` · ${t.afdeling}` : ""}</div></div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 bg-amber-200 text-amber-800">open</span>
                        </button>
                      ))}
                    </div>
                  </section>
                );
              })()}
              <section className="bg-white rounded-xl p-4 shadow-sm space-y-3" data-testid="m-reg-lijst">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-slate-800 flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-[#1C64B0]" /> Registratieformulieren</div>
                  <button data-testid="m-reg-audit-open" onClick={() => setRegView("audit")} className="text-xs font-bold text-[#1C64B0]">Per dag ›</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="text-xs font-medium text-slate-500 mb-1">Datum</div><input type="date" data-testid="m-reg-datum" className={inp} value={regDatum} onChange={(e) => setRegDatum(e.target.value)} /></div>
                  <div><div className="text-xs font-medium text-slate-500 mb-1">Lijn (indien nodig)</div><select data-testid="m-reg-lijn" className={inp} value={regLijn} onChange={(e) => setRegLijn(e.target.value)}><option value="">—</option>{regLijnOpties.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
                </div>
                <div><div className="text-xs font-medium text-slate-500 mb-1">Medewerker (indien nodig)</div><input data-testid="m-reg-medew" className={inp} value={regMedew} onChange={(e) => setRegMedew(e.target.value)} placeholder={user?.name || ""} /></div>
              </section>
              {regTpls.filter((tp) => tp.actief !== false).map((tpl) => {
                const ctx = { datum: regDatum, week: regWeek(regDatum), lijn: regLijn, medewerker: regMedew };
                const list = subsForContext(data, tpl, ctx);
                const done = list.filter((s) => s.status === "afgerond").length;
                return (
                  <button key={tpl.id} data-testid={`m-reg-open-${tpl.id}`} onClick={() => openTplList(tpl)} className="w-full text-left bg-white rounded-xl p-4 shadow-sm flex items-center justify-between gap-2">
                    <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{tpl.titel}</div><div className="text-[11px] text-slate-400 truncate">{tpl.code} · {tpl.periode === "week" ? "per week" : "per dag"}{tpl.perLijn ? " · per lijn" : ""}{tpl.perMedewerker ? " · per medewerker" : ""}</div></div>
                    {list.length ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 bg-[#1C64B0]/10 text-[#1C64B0]">{list.length} formulier{list.length > 1 ? "en" : ""}{done ? ` · ${done} afgerond` : ""}</span> : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 shrink-0">open</span>}
                  </button>
                );
              })}
              {regCanBuild && <button data-testid="m-reg-nieuw" onClick={() => { setRegBuilderInit(null); setRegView("bouwer"); }} className="w-full rounded-xl border-2 border-dashed border-[#1C64B0]/40 text-[#1C64B0] font-semibold py-3"><Plus className="h-4 w-4 inline" /> Nieuw formulier maken</button>}
            </>)}
            {regView === "kies" && regTplList && (() => {
              const ctx = { datum: regDatum, week: regWeek(regDatum), lijn: regLijn, medewerker: regMedew };
              const list = subsForContext(data, regTplList, ctx);
              return (
                <>
                  <section className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-2">
                      <button data-testid="m-reg-kies-back" onClick={() => setRegView("lijst")} className="text-slate-500"><ArrowLeft className="h-5 w-5" /></button>
                      <div className="min-w-0"><div className="font-bold text-slate-800 truncate">{regTplList.titel}</div><div className="text-[11px] text-slate-400 truncate">{regTplList.code} · {regDatum}{regLijn ? ` · ${regLijn}` : ""}</div></div>
                    </div>
                  </section>
                  {list.length === 0 ? (
                    <section className="bg-white rounded-xl p-4 shadow-sm text-sm text-slate-400" data-testid="m-reg-kies-leeg">Nog geen formulier ingevuld voor deze {regTplList.periode === "week" ? "week" : "dag"}{regLijn ? ` op ${regLijn}` : ""}.</section>
                  ) : list.map((s) => {
                    const prod = subProduct(s, regTplList);
                    return (
                      <button key={s.id} data-testid="m-reg-kies-row" onClick={() => openExistingSub(regTplList, s)} className="w-full text-left bg-white rounded-xl p-4 shadow-sm flex items-center justify-between gap-2">
                        <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{prod || "— geen product —"}</div><div className="text-[11px] text-slate-400 truncate">{s.medewerker ? `${s.medewerker} · ` : ""}bijgewerkt {(s.updatedAt || "").slice(0, 16).replace("T", " ")}</div></div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${s.status === "afgerond" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.status === "afgerond" ? "afgerond" : "bezig"}</span>
                      </button>
                    );
                  })}
                  <button data-testid="m-reg-kies-nieuw" onClick={() => openNewSub(regTplList)} className="w-full rounded-xl border-2 border-dashed border-[#1C64B0]/40 text-[#1C64B0] font-semibold py-3"><Plus className="h-4 w-4 inline" /> Nieuw formulier (ander product)</button>
                </>
              );
            })()}
            {regView === "invullen" && regTpl && regSub && (<>
              <section className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <button data-testid="m-reg-back" onClick={() => setRegView(regTplList ? "kies" : "lijst")} className="text-slate-500"><ArrowLeft className="h-5 w-5" /></button>
                  <div className="min-w-0"><div className="font-bold text-slate-800 truncate">{regTpl.titel}</div><div className="text-[11px] text-slate-400 truncate">{regTpl.code} · {regSub.periode === "week" ? `week ${regSub.week}` : regSub.datum}{regSub.lijn ? ` · ${regSub.lijn}` : ""}{regSub.medewerker ? ` · ${regSub.medewerker}` : ""}</div></div>
                  {regSub.status === "afgerond" && <span className="ml-auto text-emerald-600 flex items-center gap-1 text-xs font-bold shrink-0"><Lock className="h-3.5 w-3.5" /> afgerond</span>}
                </div>
              </section>
            {regSub.bron === "kalibratie" && (
              <section className="bg-amber-50 border border-amber-200 rounded-xl p-3 shadow-sm" data-testid="m-kal-koppel">
                <div className="text-sm text-amber-900"><span className="font-bold">Kalibratie</span> · grondstof <b>{regSub.kalibratieGrondstof || "—"}</b>{regSub.kalibratieBatchCode ? <> · tracecode <span className="font-mono">{regSub.kalibratieBatchCode}</span></> : null}</div>
              </section>
            )}
            {(() => {
                const allDocs = (data?.productieDocs || []);
                if (!allDocs.length) return null;
                const huidig = regHuidigProduct(regTpl, regData);
                const dprod = (dd) => Array.isArray(dd.producten) ? dd.producten : (dd.product ? [dd.product] : []);
                const matched = huidig ? allDocs.filter((dd) => { const ps = dprod(dd); return ps.length === 0 || ps.includes(huidig); }) : allDocs;
                const shown = matched.length ? matched : allDocs;
                return (
                  <section className="bg-white rounded-xl p-4 shadow-sm" data-testid="m-prodform-ref">
                    <div className="text-sm font-bold text-slate-700 mb-2">Productieformulier{huidig ? ` — ${huidig}` : ""} (referentie)</div>
                    <div className="space-y-1.5">
                      {shown.map((docItem) => (
                        <a key={docItem.id} data-testid="m-prodform-open" href={`${API}/files/${docItem.path}?auth=${token}`} target="_blank" rel="noreferrer" className="block truncate rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#1C64B0] active:bg-slate-50">{dprod(docItem).length ? `${dprod(docItem).join(", ")} — ` : ""}{docItem.documentnummer || docItem.bestandsnaam}{docItem.versie ? ` (${docItem.versie})` : ""}</a>
                      ))}
                    </div>
                  </section>
                );
              })()}
              <RegFormFiller tpl={regTpl} data={regData} onData={setRegData} user={user} locked={regSub.status === "afgerond" || regSaving || !!pendingRegRef.current} productOpties={productOpties} traceOpties={traceOpties} />
              <section className="bg-white rounded-xl p-4 shadow-sm">
                {regSub.status === "afgerond" ? (
                  <button data-testid="m-reg-heropen" disabled={regSaving} onClick={heropenReg} className="w-full rounded-xl border border-slate-300 py-3 font-bold text-slate-600 disabled:opacity-60">Heropenen</button>
                ) : (
                  <div className="flex gap-2">
                    <button data-testid="m-reg-save" disabled={regSaving} onClick={() => saveRegForm(false)} className="flex-1 rounded-xl border border-[#1C64B0] py-3 font-bold text-[#1C64B0] disabled:opacity-60">{regSaving ? "…" : "Tussentijds opslaan"}</button>
                    <button data-testid="m-reg-afronden" disabled={regSaving} onClick={() => saveRegForm(true)} className="flex-1 rounded-xl bg-[#1C64B0] text-white py-3 font-bold disabled:opacity-60">{regSub.periode === "week" ? "Week afronden" : "Dag afronden"}</button>
                  </div>
                )}
              </section>
            </>)}
            {regView === "audit" && (() => {
              const subs = regInzendingen(data).filter((s) => { if (s.verwijderd) return false; const tpl = regTpls.find((t) => t.id === s.sjabloonId); return tpl && inzendingOpDag(s, tpl, regAuditDatum); });
              return (<>
                <section className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2"><button data-testid="m-reg-audit-back" onClick={() => setRegView("lijst")} className="text-slate-500"><ArrowLeft className="h-5 w-5" /></button><div className="font-bold text-slate-800">Registraties per dag</div></div>
                  <input type="date" data-testid="m-reg-audit-datum" className={inp} value={regAuditDatum} onChange={(e) => setRegAuditDatum(e.target.value)} />
                  <div className="text-xs text-slate-400 mt-2" data-testid="m-reg-audit-count">{subs.length} formulier(en) op deze dag.</div>
                </section>
                {subs.length === 0 ? <div className="text-sm text-slate-400 bg-white rounded-xl p-4 text-center">Geen registraties op deze dag.</div> : subs.map((s) => (
                  <button key={s.id} data-testid="m-reg-audit-row" onClick={() => { const tpl = regTpls.find((t) => t.id === s.sjabloonId); if (tpl) openExistingSub(tpl, s); }} className="w-full text-left bg-white rounded-xl p-4 shadow-sm flex items-center justify-between gap-2">
                    <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{s.titel}</div><div className="text-[11px] text-slate-400 truncate">{s.code}{s.lijn ? ` · ${s.lijn}` : ""}{s.medewerker ? ` · ${s.medewerker}` : ""}{s.periode === "week" ? ` · week ${s.week}` : ""}</div></div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${s.status === "afgerond" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.status === "afgerond" ? "afgerond" : "bezig"}</span>
                  </button>
                ))}
              </>);
            })()}
            {regView === "bouwer" && (
              <section className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3"><button onClick={() => setRegView("lijst")} className="text-slate-500"><ArrowLeft className="h-5 w-5" /></button><div className="font-bold text-slate-800">Nieuw formulier</div></div>
                <RegFormBuilder initial={regBuilderInit} onSave={saveRegTemplate} onCancel={() => setRegView("lijst")} />
              </section>
            )}
          </>)}
        </div>
      )}

      {pakItems && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-3" data-testid="m-pakbon-modal">
          <div className="w-full max-w-xl max-h-[85vh] overflow-auto rounded-2xl bg-white p-4">
            <div className="flex items-center justify-between mb-2"><div className="font-bold text-slate-900">{t("pakControleren")} ({pakItems.length})</div><button data-testid="m-pakbon-close" onClick={() => setPakItems(null)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              {pakItems.map((it) => (
                <div key={it._id} data-testid={`m-pakbon-item-${it._id}`} className="rounded-xl border border-slate-200 p-3 space-y-2">
                  <div className="flex items-center justify-between"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${it.bestaand ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{it.bestaand ? t("bestaand") : t("nieuw")}</span><button data-testid={`m-pakbon-del-${it._id}`} onClick={() => removePakItem(it._id)} className="text-rose-600"><X className="h-4 w-4" /></button></div>
                  <input className={inp} placeholder={t("phHulpstof")} data-testid={`m-pakbon-naam-${it._id}`} value={it.naam} onChange={(e) => updatePakItem(it._id, "naam", e.target.value)} />
                  <div className="grid grid-cols-2 gap-2">
                    <select className={inp} value={it.type} onChange={(e) => updatePakItem(it._id, "type", e.target.value)}>{["Predust", "Battermix", "Breader", "Ham/kaas"].map((o) => <option key={o} value={o}>{o}</option>)}</select>
                    <input className={inp} placeholder={t("phArtikelnr")} value={it.artikelnr} onChange={(e) => updatePakItem(it._id, "artikelnr", e.target.value)} />
                    <input inputMode="decimal" className={inp} placeholder={t("phKg")} data-testid={`m-pakbon-kg-${it._id}`} value={it.kg} onChange={(e) => updatePakItem(it._id, "kg", e.target.value)} />
                    <input inputMode="decimal" className={inp} placeholder={t("phPrijsKg")} value={it.prijs} onChange={(e) => updatePakItem(it._id, "prijs", e.target.value)} />
                    <input className={inp} placeholder={t("phLeverancier")} value={it.leverancier} onChange={(e) => updatePakItem(it._id, "leverancier", e.target.value)} />
                    <input className={inp} placeholder={t("phBatchnr")} value={it.batchnr} onChange={(e) => updatePakItem(it._id, "batchnr", e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4"><button data-testid="m-pakbon-cancel" onClick={() => setPakItems(null)} className="flex-1 rounded-xl border border-slate-300 py-3 font-bold text-slate-600">{t("annuleren")}</button><button data-testid="m-pakbon-confirm" onClick={confirmPakScan} className="flex-1 rounded-xl bg-[#1C64B0] text-white py-3 font-bold">{t("toevoegen")}</button></div>
          </div>
        </div>
      )}

      {grondPakItems && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-3" data-testid="m-grond-pakbon-modal">
          <div className="w-full max-w-xl max-h-[85vh] overflow-auto rounded-2xl bg-white p-4">
            <div className="flex items-center justify-between mb-2"><div className="font-bold text-slate-900">{t("pakGrondControleren")} ({grondPakItems.length})</div><button data-testid="m-grond-pakbon-close" onClick={() => setGrondPakItems(null)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              {grondPakItems.map((it) => (
                <div key={it._id} data-testid={`m-grond-pakbon-item-${it._id}`} className="rounded-xl border border-slate-200 p-3 space-y-2">
                  <div className="flex items-center justify-between"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${it.bestaand ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{it.bestaand ? t("bestaand") : t("nieuw")}</span><button data-testid={`m-grond-pakbon-del-${it._id}`} onClick={() => removeGrondPakItem(it._id)} className="text-rose-600"><X className="h-4 w-4" /></button></div>
                  <input className={inp} placeholder={t("phGrondstof")} data-testid={`m-grond-pakbon-naam-${it._id}`} value={it.naam} onChange={(e) => updateGrondPakItem(it._id, "naam", e.target.value)} />
                  {it.artChanged && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-2 py-2 text-[12px] text-amber-800" data-testid={`m-grond-pakbon-artchg-${it._id}`}>
                      {t("artChg1")} <b>{it.oldArt}</b> → <b>{it.artikelnr}</b>. {t("artChg2")}
                      <div className="mt-1 flex gap-2">
                        <button type="button" data-testid={`m-grond-pakbon-art-update-${it._id}`} onClick={() => updateGrondPakItem(it._id, "updateArt", true)} className={`flex-1 rounded-lg py-1.5 font-bold ${it.updateArt ? "bg-emerald-600 text-white" : "bg-white border border-amber-300 text-amber-800"}`}>{t("jaBijwerken")}</button>
                        <button type="button" data-testid={`m-grond-pakbon-art-keep-${it._id}`} onClick={() => updateGrondPakItem(it._id, "updateArt", false)} className={`flex-1 rounded-lg py-1.5 font-bold ${!it.updateArt ? "bg-slate-700 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>{t("neeBehouden")}</button>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <input className={inp} placeholder={t("phArtikelnr")} value={it.artikelnr} onChange={(e) => updateGrondPakItem(it._id, "artikelnr", e.target.value)} />
                    <div><input className={inp} placeholder={t("phLeverancier")} value={it.leverancier} onChange={(e) => updateGrondPakItem(it._id, "leverancier", e.target.value)} />{it.levConflict && <div className="mt-1 text-[11px] text-amber-600">{t("pakbonLbl")} "{it.levScanned}" <button type="button" data-testid={`m-grond-pakbon-lev-use-${it._id}`} className="underline font-semibold" onClick={() => updateGrondPakItem(it._id, "leverancier", it.levScanned)}>{t("gebruik")}</button></div>}</div>
                    <input inputMode="decimal" className={inp} placeholder={t("phKg")} data-testid={`m-grond-pakbon-kg-${it._id}`} value={it.kg} onChange={(e) => updateGrondPakItem(it._id, "kg", e.target.value)} />
                    <input inputMode="decimal" className={inp} placeholder={t("phPrijsKg")} value={it.prijs} onChange={(e) => updateGrondPakItem(it._id, "prijs", e.target.value)} />
                    <input className={inp} placeholder={t("phBatchnrLev")} value={it.batchnr} onChange={(e) => updateGrondPakItem(it._id, "batchnr", e.target.value)} />
                    <input className={`${inp} font-semibold text-[#1C64B0]`} placeholder={t("phEigenTrace")} data-testid={`m-grond-pakbon-eigentrace-${it._id}`} value={it.eigenTracecode != null ? it.eigenTracecode : mEigenTrace(it)} onChange={(e) => updateGrondPakItem(it._id, "eigenTracecode", e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4"><button data-testid="m-grond-pakbon-cancel" onClick={() => setGrondPakItems(null)} className="flex-1 rounded-xl border border-slate-300 py-3 font-bold text-slate-600">{t("annuleren")}</button><button data-testid="m-grond-pakbon-confirm" onClick={confirmGrondPakScan} className="flex-1 rounded-xl bg-[#1C64B0] text-white py-3 font-bold">{t("toevoegen")}</button></div>
          </div>
        </div>
      )}

      {divOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-3" data-testid="m-diversen-modal">
          <div className="w-full max-w-xl max-h-[88vh] overflow-auto rounded-2xl bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold text-slate-900">Diversen inboeken</div>
              <button data-testid="m-diversen-close" onClick={() => { setDivOpen(false); setDivSearch(""); }}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-3">Vul het ontvangen aantal in en tik op Inboek. Dit telt direct op bij de voorraad diversen.</p>
            <input data-testid="m-diversen-search" className={inp + " mb-3"} placeholder="Zoek doos, folie, artikelnr…" value={divSearch} onChange={(e) => setDivSearch(e.target.value)} />
            <div className="space-y-2">
              {divList.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6" data-testid="m-diversen-empty">Geen artikelen gevonden.</div>
              ) : divList.map((m) => (
                <div key={m.id} data-testid="m-diversen-row" className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{m.naam}</div>
                      <div className="text-[11px] text-slate-400 truncate">{m.artikelnummer ? m.artikelnummer + " · " : ""}{m.categorie} · {m.eenheid}{m.leverancier ? " · " + m.leverancier : ""} · voorraad {fmtAant(divStock(m))}</div>
                    </div>
                    {m.inboekBijBinnenkomst && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">binnenkomst</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <input inputMode="decimal" type="number" min="0" step="any" data-testid={`m-diversen-qty-${m.id}`} className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-base" placeholder={`Aantal (${m.eenheid || ""})`} value={divQty[m.id] ?? ""} onChange={(e) => setDivQty({ ...divQty, [m.id]: e.target.value })} />
                    <button data-testid={`m-diversen-inboek-${m.id}`} disabled={divBusy} onClick={() => inboekDiversenM(m)} className="rounded-lg bg-[#1C64B0] text-white font-semibold px-4 py-2.5 text-sm shrink-0 disabled:opacity-60">Inboek</button>
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="m-diversen-done" onClick={() => { setDivOpen(false); setDivSearch(""); }} className="mt-4 w-full rounded-xl border border-slate-300 py-3 font-bold text-slate-600">Klaar</button>
          </div>
        </div>
      )}

      {divPakItems && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-3" data-testid="m-diversen-pakbon-modal">
          <div className="w-full max-w-xl max-h-[85vh] overflow-auto rounded-2xl bg-white p-4">
            <div className="flex items-center justify-between mb-2"><div className="font-bold text-slate-900">Diversen pakbon controleren ({divPakItems.length})</div><button data-testid="m-diversen-pakbon-close" onClick={() => setDivPakItems(null)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              {divPakItems.map((it) => (
                <div key={it._id} data-testid={`m-diversen-pakbon-item-${it._id}`} className="rounded-xl border border-slate-200 p-3 space-y-2">
                  <div className="flex items-center justify-between"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${it.bestaand ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{it.bestaand ? "bestaand" : "nieuw"}</span><button data-testid={`m-diversen-pakbon-del-${it._id}`} onClick={() => removeDivPakItem(it._id)} className="text-rose-600"><X className="h-4 w-4" /></button></div>
                  <input className={inp} placeholder="Omschrijving" data-testid={`m-diversen-pakbon-naam-${it._id}`} value={it.naam} onChange={(e) => updateDivPakItem(it._id, "naam", e.target.value)} />
                  <div className="grid grid-cols-2 gap-2">
                    <input className={inp} placeholder="Artikelnr" value={it.artikelnr} onChange={(e) => updateDivPakItem(it._id, "artikelnr", e.target.value)} />
                    <input className={inp} placeholder="Eenheid (Doos/Rol…)" value={it.eenheid} onChange={(e) => updateDivPakItem(it._id, "eenheid", e.target.value)} />
                    <input inputMode="decimal" className={inp} placeholder="Aantal" data-testid={`m-diversen-pakbon-aantal-${it._id}`} value={it.aantal} onChange={(e) => updateDivPakItem(it._id, "aantal", e.target.value)} />
                    <input inputMode="decimal" className={inp} placeholder="Prijs/eenheid" value={it.prijs} onChange={(e) => updateDivPakItem(it._id, "prijs", e.target.value)} />
                    <input className={inp} placeholder="Leverancier" value={it.leverancier} onChange={(e) => updateDivPakItem(it._id, "leverancier", e.target.value)} />
                    <input className={inp} placeholder="Batch/partij" value={it.batchnr} onChange={(e) => updateDivPakItem(it._id, "batchnr", e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4"><button data-testid="m-diversen-pakbon-cancel" onClick={() => setDivPakItems(null)} className="flex-1 rounded-xl border border-slate-300 py-3 font-bold text-slate-600">{t("annuleren")}</button><button data-testid="m-diversen-pakbon-confirm" onClick={confirmDivPakScan} className="flex-1 rounded-xl bg-[#1C64B0] text-white py-3 font-bold">{t("toevoegen")}</button></div>
          </div>
        </div>
      )}

      {mobScreen === "productie" && (
      <div className="fixed bottom-0 inset-x-0 p-3 bg-white border-t border-slate-200">
        <div className="max-w-xl mx-auto">
          <div className="flex gap-2 mb-2">
            <button data-testid="m-mode-definitief" onClick={() => setMode("definitief")} className={`flex-1 rounded-lg py-2 text-sm font-bold ${mode === "definitief" ? "bg-[#1C64B0] text-white" : "bg-slate-100 text-slate-500"}`}>{t("modeDefinitief")}</button>
            <button data-testid="m-mode-concept" onClick={() => setMode("concept")} className={`flex-1 rounded-lg py-2 text-sm font-bold ${mode === "concept" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500"}`}>{t("modeConcept")}</button>
          </div>
          <button data-testid="m-save" onClick={save} disabled={saving || !productieSynced}
            className={`w-full flex items-center justify-center gap-2 rounded-xl text-white font-bold py-4 text-lg disabled:opacity-60 ${mode === "concept" ? "bg-violet-600 hover:bg-violet-700" : "bg-[#1C64B0] hover:bg-[#154E8A]"}`}>
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
            {saving ? t("opslaanBezig") : mode === "concept" ? t("modeConcept") : online ? t("opslaan") : t("opslaanOffline")}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
